import {
  aliasKind, hasRecallChip, probeValuesForRecalls,
  angleModeFor, addVersion, applyOptionsVersion, branchSetsToCheck, removeVersion, versionRollKey, writeOptionsVersion, visibleTargeted, roleForMode, rolesForMode, isReachable, displayEvaluation, branchRoleIssue, dropdownReachability, branchesUnreachableInRange, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount, checkOptionFormulaIssues, evaluateDropdown, evaluateEquation, syncScenarioBranches, type ScenarioLeaderVersion, remapVersionsToScenarios, drawnScenarioId, markScenarioReviewed, unreviewedCount, type RoleVersion, pickScenarioBranch, resolveScenarioOptions, scenarioInForce, type DisplayVersion, type ScenarioRoleBranch, getEquationVariableNames, parseThreshold, resolveEffectiveOptions, resolveMatchedBranch, TRIG_CALL_PATTERN, activeFeedbackForBranch , type AngleMode, type ConditionalRoleBranch, type DropdownBlockData, type DropdownFeedback, type DropdownOption, type FeedbackState } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronDown, GripVertical, Minus, Pin, SquareChevronDown, Trash2, X } from 'lucide-react';
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope, useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { type BlockIssue, duplicateKeyIgnoringBlank, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass, UnreachableChip } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, ChipDivider, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { FormulaField, FormulaWithValues } from './formula-field';
import { checkOptionChipCollisions, richTextToFormula as optionTextToFormula, stripHtmlForLabel } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtLocale } from './number-format';
import { reorderByIds, shuffleWithAnchors } from './option-order';
import { OptionsSection } from './options-section';
import { recallOptionSpaces, recallSpacesKey } from './recall-option-spaces';
import { RichTextEditor } from './rich-text-editor';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { StrandWarning, useArmSettle } from './strand-warning';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenRowDeleteWarning } from './use-driven-row-dependents';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, hasEmptyFeedbackContent, normalizeFeedback, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { makeRowBranch, nestedBranching, type SeedMode } from './use-nested-branching';
import { useRecallAffordance } from './use-recall-affordance';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { useDelayedStickyError, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useVariableMechanismAllowed } from './use-variable-affordance';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockQuestion, AnswerDisplayFrame, formulaToRichText, hasRollableVariables, htmlHasVariableChips, recallAliasIdsIn, labelChipHtml, PreviewHtml, processVariableChipHTML, renderChipsForDisplay, rollVariableValues, useChipScope, variableChipNames, type ChipScope } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { authoredPreviewNode, catchAllTitle, legendStatesFor, catchAllChips, dropdownChips } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { dropdownOptionLabelText } from '@/lib/block-state-labels';
import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

/** Bridges "jump to this branch" from the live-test widget (the collapsed canvas card) to the
 * editor — they're separate mounted instances of this component (one rendered collapsed/
 * isSelected, one via InputBlockFrame, each with its own local state seeded once at mount — see
 * the isSelected/isEditing doc comment in the component body), with no other channel to hand off
 * a value like this.
 *
 * Two paths, because the editor may or may not already be mounted when the jump is requested:
 * - Already open (the common case — a teacher testing with the editor panel visible alongside):
 *   `branchJumpListeners` has a live subscriber, called directly for an immediate update. A plain
 *   mount-time-only hand-off would silently never take effect here, since nothing re-runs a
 *   `useState` lazy initializer on an already-mounted component.
 * - Not yet open: falls back to `pendingBranchJump`, consumed once at the editor's own mount and
 *   cleared immediately after, so it never overrides a teacher's own subsequent navigation.
 *
 * Deliberately NOT automatic on every reroll — only fires when the teacher explicitly clicks the
 * jump action. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface DropdownBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped once per click of Preview's single bottom Submit — see task-content.tsx's own doc
   * comment. Only meaningful while `isPreview`; unused otherwise. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** A new option, for an event handler. NEVER call this during render — see DEFAULT_OPTIONS. */
function makeOption(role: DropdownOption['role'] = 'incorrect'): DropdownOption {
  return { id: crypto.randomUUID(), text: '', role, anchored: false };
}

/** The starter options, with FIXED ids — `initData` runs during render, and minting ids there
 * changes an option's identity on every render. Ids need only be unique within one block. */
const DEFAULT_OPTIONS: readonly DropdownOption[] = [
  { id: 'default-option-1', text: '', role: 'correct', anchored: false },
  { id: 'default-option-2', text: '', role: 'incorrect', anchored: false },
  { id: 'default-option-3', text: '', role: 'incorrect', anchored: false },
];

function initData(raw: Record<string, unknown>, placeholderDefault: string): DropdownBlockData {
  type RawOption = { id: string; text: string; formula?: string; role?: DropdownOption['role']; anchored?: boolean };
  const opts = raw.options as RawOption[] | undefined;
  const feedback = (raw.feedback ?? { states: [] }) as DropdownBlockData['feedback'];
  // Branch-owned feedback (see ConditionalRoleBranch in @agavi/blocks) is newer than
  // conditionalBranches itself — a dropdown whose branches were saved before that field existed
  // has branch objects with no `feedback` at all. Reading `.feedback.states` off one of those
  // unconditionally crashes the whole render tree, not just this block, so every branch is
  // backfilled here before anything downstream ever sees it. Backfilled from the top-level
  // feedback (fresh ids) rather than left empty, matching the same "migrate" default a freshly
  // toggled-on first branch gets.
  type RawBranch = Omit<ConditionalRoleBranch, 'feedback'> & { feedback?: ConditionalRoleBranch['feedback'] };
  const rawBranches = raw.conditionalBranches as RawBranch[] | undefined;
  const conditionalBranches: ConditionalRoleBranch[] | undefined = rawBranches?.map(b => ({
    ...b,
    // Deterministic, not `crypto.randomUUID()`. `initData` runs during render, so minting here
    // gave every render a fresh set of state ids for any branch that has not authored its own
    // feedback — and a feedback state's id is what `firedIds`, Flow rules and targeted triggers
    // all match on. Same fault that made a fresh MCQ grade its own correct answer wrong.
    feedback: b.feedback ?? { states: feedback.states.map(s => ({ ...s, id: `${b.id}-${s.id}` })) },
  }));
  return {
    question: (raw.question as string) ?? '',
    options: opts?.length
      ? opts.map(o => ({ id: o.id, text: o.text, formula: o.formula, role: o.role ?? 'incorrect', anchored: o.anchored ?? false }))
      : DEFAULT_OPTIONS.map(o => ({ ...o })),
    randomizeOrder: (raw.randomizeOrder as boolean) ?? false,
    placeholder: (raw.placeholder as string) ?? placeholderDefault,
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
      ? raw.gradingMode as DropdownBlockData['gradingMode']
      : 'graded'),
    feedback,
    conditionalEquation: raw.conditionalEquation as DropdownBlockData['conditionalEquation'],
    conditionalBranches,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as DropdownBlockData['conditionalBranchesDraft'],
    optionsAsEquations: (raw.optionsAsEquations as boolean) ?? false,
    angleMode: (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    /* This builds a NEW object field by field, so anything not named here is dropped. Leaving
       `versions` out is how MCQ's bank came to be created, emitted, written to Mongo and then
       silently gone the moment the block re-read its own data — nothing failed, it was just
       absent. Pinned by `mcq-versions.test.tsx`. */
    versions: raw.versions as DropdownBlockData['versions'],
    /* Same hazard, same fix: a scenario link that is read back as absent is a block that
       silently stops following, with its per-scenario answers orphaned behind it. */
    followsBlockId: raw.followsBlockId as DropdownBlockData['followsBlockId'],
    /* And the third one, found the same way: without this line the driven link was dropped on
       every read, so the projection never ran and every write erased what the menu had just
       stored. Three fields, one hazard — see the `versions` note above. */
    drivenBy: raw.drivenBy as DropdownBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as DropdownBlockData['scenarioBranches'],
  };
}

/** Resolves what an option should actually DISPLAY as — every reader of an option's content
 * (chip legend, live select, feedback titles, Default Correct/Incorrect) goes through this instead
 * of reading `.text` directly, so "which mode are we in" is decided in exactly one place. When
 * `optionsAsEquations` is on, the option's `formula` is evaluated and the RESULT is what's shown
 * (a plain formatted number, never the formula string itself — a student should never see
 * "{var1}+{var2}", only "8"); otherwise falls through to `.text` exactly as before this feature
 * existed. Blank/unevaluable stays blank, same "obviously incomplete, never a placeholder"
 * treatment already established for every other blank-option case — this is a HTML-safe plain
 * string either way (a formatted number can't contain markup), safe to feed anywhere `.text`
 * (rich HTML) was previously read, including `dangerouslySetInnerHTML`. */
function dropdownOptionDisplayText(
  option: DropdownOption,
  optionsAsEquations: boolean | undefined,
  resolvedVariables: Record<string, number>,
  locale: string,
  angleMode: AngleMode,
): string {
  if (!optionsAsEquations) return option.text;
  if (!option.formula || !option.formula.trim()) return '';
  const v = evaluateEquation(option.formula, resolvedVariables, angleMode);
  return v === null ? '' : fmtLocale(v, locale);
}

/** Human-readable label for a single option — its own text, or blank when the option itself is
 * blank. Deliberately does NOT fall back to a positional "Option 2" placeholder: that would read
 * as real, already-authored content and hide the fact that the option needs to be filled in —
 * an obviously-incomplete label (nothing) is the honest signal, matching the same
 * blank-stays-blank treatment used for the chip legend and Default Correct/Incorrect summaries. */
/** Derived in `block-state-labels` now, so Flow reads the same function this writes with. */
const dropdownOptionLabel = dropdownOptionLabelText;

/** The mechanically-derived, non-editable title for a targeted feedback state — always
 * reflects exactly which option it targets, so two feedbacks can only collide on title when
 * they genuinely target the same option (a real setup error), never by coincidence. */
function dropdownTargetedTitle(option: DropdownOption, t: (key: string, opts?: Record<string, unknown>) => string, optionsAsEquations?: boolean): string {
  return `${t('blocks.answers.selectedPrefix')}${dropdownOptionLabel(option, optionsAsEquations)}`;
}

/** Re-derives every targeted feedback title against the current options array — needed
 * whenever an option's text changes (position no longer matters — a blank option's label is
 * just blank, not positional), and any option's title embeds its current text. */
function resyncDropdownTargetedTitles(options: DropdownOption[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string, optionsAsEquations?: boolean): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return s;
    const id = s.trigger.slice('option_'.length);
    const idx = options.findIndex(o => o.id === id);
    if (idx === -1) return s;
    return { ...s, title: dropdownTargetedTitle(options[idx]!, t, optionsAsEquations) };
  });
}

/** Targeted feedback's evaluation always mirrors its target option's role — Dropdown enforces
 * exactly one correct option at all times (radio behavior), so a feedback's own target option
 * stays 'incorrect' unless a teacher later clicks that exact option (making it the new correct
 * one) — reachable in the static case, and routine per-branch (a different option is often
 * correct in different branches). Derived from the option's own role rather than hardcoded, so
 * the two can never silently drift apart.
 *
 * Includes `role === 'correct'` — this state becomes functionally unreachable the instant its
 * own option is correct (correct-first-terminal means it's never even consulted), so the actual
 * evaluation value stops mattering at runtime either way, but it must still update: Flow's
 * branch-collapsing (flow-utils.ts's feedbackStatesOf) relies on evaluation === 'correct' to
 * detect "this branch's copy is dead, exclude it from this condition's scope" — leaving it stale
 * here silently broke that detection, letting a targeted state for a now-correct option keep
 * showing up as a live Flow condition. The block's own editor was never affected by the staleness
 * (the editor determines visibility via a direct role lookup, not this
 * field), which is exactly why this went unnoticed until Flow started trusting evaluation. */
function resyncDropdownTargetedEvaluations(options: DropdownOption[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return s;
    const id = s.trigger.slice('option_'.length);
    const opt = options.find(o => o.id === id);
    if (!opt) return s;
    return { ...s, evaluation: opt.role };
  });
}

/** Resolves the human-readable "condition" a feedback state represents, for the teacher-facing
 * test bar legend — reused across every trigger kind rather than showing raw internal trigger
 * strings, and across both a fired state (during/after a test) and every not-yet-fired state
 * (before any test has run, or one that just didn't fire this time), so the same legend renders
 * as a permanent chip list either way. `option_<id>` triggers always resolve to that exact
 * option, submission or no. `correct`/`incorrect` have no single option baked into their trigger
 * (any correct/incorrect option satisfies them): once `submittedId` names one, that's the
 * concrete option shown; with no submission yet, `correct` falls back to whichever option is
 * currently marked correct (always exactly one, by Dropdown's own invariant), and `incorrect`
 * falls back to its generic title label, since no single option represents it. `any_answer`
 * (survey) and `any_response` (optional) never name a specific option at all — any selection (or
 * none, for any_response) satisfies them, so they always show their generic label. */
/**
 * Dropdown's two default conditions as one chip list — the derivation the feedback panel and the
 * test legend both draw.
 *
 * The two surfaces were furthest apart here of any block. Default Incorrect at least fanned out on
 * both sides (with a comment on the legend's copy noting they "already agreed" — which is a
 * statement about today, not a mechanism). Default CORRECT did not: the panel fanned out one chip
 * per correct option joined by "or", while the legend showed a single `dropdownStateCondition`
 * chip for the whole state. One answer key, two shapes.
 *
 * `correctOptions` comes from the shared reachability, whose own doc calls it "what Default Correct
 * names" — the panel had been filtering it by targeted coverage instead, so a correct option with
 * stale targeted feedback dropped out of the answer key.
 *
 * Both lists join with "or": a dropdown takes ONE selection, so its options are alternatives, never
 * a conjunction. This is the difference from MCQ multi-select, and it is why this block cannot just
 * reuse `mcqChips`.
 *
 * Default Incorrect ENUMERATES. The option set is closed, so the complement of the correct answer
 * is a short known list and there is no "any other selection" to fall back on.
 */
/** Moved to `lib/block-condition-chips` — see that file's note. */

/**
 * One option, drawn the way every chip surface draws it.
 *
 * Was a closure inside the editor (`optContent`) while the legend went through
 * `dropdownStateCondition` — two renderings of one option, which is the same split this whole phase
 * is closing. Lifted so both can call it.
 *
 * Chips on BOTH branches. The equation branch used to render plain text (literal `{n}` braces) and
 * the rich-text one went through `bracketizeVariableChips`, which turns a chip span back into
 * `{name}` text — two routes to the same violation of the no-bare-braces rule.
 *
 * WHICH option is passed decides the resolution, and that is the caller's business: the feedback
 * panel passes the authored option so a variable stays a chip, the legend passes the resolved one
 * so it shows the rolled value.
 */
function dropdownOptionNode(
  opt: DropdownOption, optionsAsEquations: boolean | undefined, chipScope?: ChipScope,
): React.ReactNode {
  if (optionsAsEquations) {
    const formula = opt.formula?.trim();
    return formula
      ? <span className='min-w-0 flex-1 overflow-hidden whitespace-nowrap'><FormulaWithValues formula={formula} rolledValues={{}} /></span>
      : null;
  }
  const inner = opt.text.replace(/<\/?p>/g, '').trim();
  if (!inner) return null;
  return (
    <span className='min-w-0 flex-1 overflow-hidden whitespace-nowrap ws-formula-preview' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(renderChipsForDisplay(inner, chipScope)) }} />
  );
}

function dropdownStateCondition(
  state: FeedbackState, submittedId: string | null, d: DropdownBlockData, t: (key: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
  // No positional "Option N" fallback here — a blank option's chip should look obviously
  // incomplete (icon + empty label), not like real, already-authored content.
  const optionLabelNode = (opt: DropdownOption): React.ReactNode => {
    const inner = opt.text.replace(/<\/?p>/g, '').trim();
    return inner
      /* NO SCOPE MARK, and none is reachable: the only caller is `computeDropdownTestData`,
         which passes `resolvedD` — options whose chips are already values. */
      ? <span className='ws-formula-preview' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(renderChipsForDisplay(inner)) }} />
      : null;
  };
  if (state.trigger === 'unanswered') return <>{t('blocks.feedbackDefaults.noAnswerTitle')}</>;
  if (state.trigger === 'any_answer') return <>{t('blocks.feedbackDefaults.anyAnswerTitle')}</>;
  if (state.trigger === 'any_response') return <>{t('blocks.feedbackDefaults.anyResponseTitle')}</>;
  if (state.trigger === 'correct') {
    const optionId = submittedId ?? d.options.find(o => o.role === 'correct')?.id ?? null;
    if (optionId == null) return null;
    const idx = d.options.findIndex(o => o.id === optionId);
    return idx === -1 ? null : optionLabelNode(d.options[idx]!);
  }
  if (state.trigger === 'incorrect') {
    if (submittedId == null) return <>{t('blocks.feedbackDefaults.incorrectTitle')}</>;
    const idx = d.options.findIndex(o => o.id === submittedId);
    return idx === -1 ? <>{t('blocks.feedbackDefaults.incorrectTitle')}</> : optionLabelNode(d.options[idx]!);
  }
  const optionId = state.trigger.startsWith('option_') ? state.trigger.slice('option_'.length) : null;
  if (optionId == null) return null;
  const idx = d.options.findIndex(o => o.id === optionId);
  return idx === -1 ? null : optionLabelNode(d.options[idx]!);
}

/** True when a dropdown's conditional-correctness setup is enabled but broken in some way that
 * means a student could get a role resolution the teacher never actually configured — an empty or
 * unparseable equation (falls back to each option's plain static role, silently), a coverage
 * gap/overlap in the branch thresholds, or a branch with zero/multiple options marked correct.
 * Shared between the collapsed card's warning bar and the open editor's own inline warnings so
 * the two can never disagree about whether the setup is actually broken. */
// ─── Unified issue reporting ────────────────────────────────────────────────────
// One computation, read by every surface (collapsed bar, test-panel diagnostics, in-editor
// banner) instead of each maintaining its own similar-but-not-identical checks — the single
// cause of both problems this was built to fix: the same issue reading differently depending on
// where you looked, and some issues (blank question/options/correct option) only ever having
// been computed in the collapsed view, never inside the editor itself.

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `dropdownIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function dropdownBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return dropdownIssues(buildNormalizedDropdownData(initData(block.data, ctx.t('blocks.dropdown.placeholderDefault')), ctx.t), ctx);
}

export function dropdownIssues(d: DropdownBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables, recalls = {} } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryAlternativeCorrectStates = t('blocks.areas.alternativeAnswerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  /*
   * UNREVIEWED ROWS, counted — the flag's one weakness, closed.
   *
   * The strip inside the box only shows for the row you are on, so a teacher who never pages to
   * scenario 5 never learns that scenario 5 was never decided. That is the exact failure the
   * flag exists to prevent, moved one level out: work that looks finished and is not.
   *
   * An INCOMPLETION, not a defect — every row has a working answer key; what is missing is
   * somebody's confirmation that the copy is right for that scenario. A warning, because the
   * block grades perfectly well in the state; `kind` is what makes it count at publication.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  // In equations mode, checks `formula` OR `text`, deliberately not formula-only — see MCQ's own
  // identical `optionIsBlank` doc comment for why (turning the toggle on shouldn't retroactively
  // make a pre-existing, still-real option look blank before its new formula is even authored).
  const optionIsBlank = (o: DropdownOption) =>
    d.optionsAsEquations ? (!o.formula?.trim() && !o.text.replace(/<\/?p>/g, '').trim()) : !o.text.replace(/<\/?p>/g, '').trim();
  const questionBlank = !d.question.replace(/<\/?p>/g, '').trim();
  const allOptionsBlank = d.options.every(optionIsBlank);

  if (questionBlank) issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  if (allOptionsBlank) {
    issues.push({ key: 'options-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.allBlankWarning') });
  } else if (d.gradingMode === 'graded') {
    // Once branching is enabled, the STATIC role assignment is vestigial for grading purposes —
    // resolveEffectiveOptions always consults the branches instead, falling back to static roles
    // only when conditionalBranches is unset entirely. Checking the static correct option's text
    // here too (alongside every branch's own) would flag the same underlying "the correct answer
    // has no text" problem twice for the common case where a fresh branch's roles start as a
    // copy of the static ones. Static check only applies when there's no branching to defer to.
    if (d.conditionalBranches && d.conditionalBranches.length > 0) {
      d.conditionalBranches.forEach((b, i) => {
        const branchCorrect = d.options.find(o => b.roles[o.id] === 'correct');
        if (branchCorrect && optionIsBlank(branchCorrect)) {
          issues.push({ key: `branch-${b.id}-correct-blank`, severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.diagnosticBranchCorrectBlank', { number: i + 1 }) });
        }
      });
    } else {
      const staticCorrect = d.options.find(o => o.role === 'correct');
      if (staticCorrect && optionIsBlank(staticCorrect)) {
        issues.push({ key: 'correct-option-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.correctBlankWarning') });
      }
    }
  }
  // A blank DISTRACTOR (not the correct option, already covered above) is exactly as confusing
  // to a student as a blank Ordering item — mirrors MCQ's own identical parity fix (2026-08-02),
  // see its own doc comment. Not gated on `allOptionsBlank`/`gradingMode`.
  if (d.optionsAsEquations && d.options.some(o => !o.formula?.trim())) {
    issues.push({ key: 'option-formula-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaBlankWarning') });
  }

  // Exact-duplicate option text is a real fairness hazard, not just clutter — a student can't tell
  // two identical-looking options apart, so passability depends on blind luck rather than
  // understanding, the same fairness standard already used for optionsAsEquations' own
  // value-collision check. Skipped entirely in optionsAsEquations mode — two IDENTICAL formulas
  // are already guaranteed to collide on every roll, a case checkOptionFormulaIssues' own (more
  // general, evaluated-value) collision detection already catches; comparing raw formula strings
  // here would just be a redundant, weaker version of that check. Deliberately exact string
  // equality on the raw stored HTML, not case/accent-normalized or stripped-to-plain-text — two
  // options that only look the same after a formatting difference (e.g. one bold, one not) are NOT
  // flagged, only options whose stored content is byte-for-byte identical. `duplicateKeyIgnoringBlank`
  // (not a bare `.trim()`) is load-bearing, not decorative — see its own doc comment in
  // block-issues.tsx for the real bug this closes (blank options otherwise falsely flag each other).
  if (!d.optionsAsEquations) {
    for (const group of findDuplicateGroups(d.options, o => duplicateKeyIgnoringBlank(o.text))) {
      issues.push({
        key: `duplicate-${group.map(o => o.id).join('-')}`,
        fields: group.map(o => o.id),
        severity: 'error',
        kind: 'defect',
        category: categoryAnswerSetup,
        detail: t('blocks.options.duplicateOptionWarning', { text: stripHtmlForLabel(group[0]!.text) }),
      });
    }
  }

  // Everything below requires an explicit action to even become possible (turning on branching,
  // typing an equation, marking a role) — you can't reach any of these by simply leaving a
  // brand-new block alone, so none of them need the grace period above. Deliberately NOT gated on
  // `!allOptionsBlank` either — equation validity, coverage, and role issues are independent of
  // whether option TEXT happens to be filled in; gating on it here (as an earlier version did)
  // silently swallowed every equation/coverage/role error the moment options were blank, which is
  // exactly backwards — those are the cases most likely to occur on a block that's still being set
  // up, i.e. exactly when someone would still want to see them.
  /* PER VERSION, not once for whatever version is on screen: these checks read version-resolved
     data, so a banked block was only ever validated for the version being displayed and a gap
     authored in version 3 stayed invisible. See `branchSetsToCheck`. */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalRoleBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') })) });
        }
        if (overlapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        const roleIssue = branchRoleIssue(b, d.options);
        if (roleIssue === 'none-correct') issues.push({ key: vkey(`branch-${b.id}-none`), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.options.diagnosticBranchNoneCorrect', { number: i + 1 })) });
        if (roleIssue === 'multiple-correct') issues.push({ key: vkey(`branch-${b.id}-multi`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.options.diagnosticBranchMultipleCorrect', { number: i + 1 })) });
        if (b.operator === 'between') {
          const lo = parseThreshold(b.threshold);
          const hi = parseThreshold(b.thresholdHigh);
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Both errors, not warnings — a formula that can fail to evaluate for some in-range roll can
  // strand a student with no reachable correct answer (refreshing doesn't reroll), the same
  // severity a branch coverage gap already has; a collision lets a student who reasoned correctly
  // still have to blind-guess between two identical-looking options, making passability depend on
  // luck of the roll, the same fairness standard already used to rule out independent per-option
  // predicates elsewhere in this design.
  if (d.optionsAsEquations) {
    // Every variable, not just the rollable ones: a constant or an equation is as referenceable
    // from an option formula as a random, and filtering them out reported the option unevaluable.
    const formulaVars = variables;
    const { issues: formulaIssues } = checkOptionFormulaIssues(d.options, formulaVars, id => angleModeFor(d, `options.${id}.formula`));
    for (const issue of formulaIssues) {
      if (issue.kind === 'unevaluable') {
        issues.push({ key: `option-${issue.optionIds[0]}-unevaluable`, fields: issue.optionIds, severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaUnevaluable') });
      }
    }
    // One issue total, not one per colliding pair — with 3+ options all pairwise colliding
    // (A-B, A-C, B-C), a teacher doesn't need the identical message three times over.
    if (formulaIssues.some(i => i.kind === 'collision')) {
      issues.push({ key: 'option-collision', fields: formulaIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaCollision') });
    }
  } else {
    // Warning, not error — see mcq-block.tsx's identical wiring for the full reasoning: a
    // plain-text option embedding a variable chip (e.g. "Mama {n}" / "Mama {m}") colliding for
    // some roll is a coincidence of the chosen ranges, not a guaranteed-identical pair the way a
    // full-formula collision is.
    const chipVars = variables;
    const { issues: chipIssues } = checkOptionChipCollisions(d.options, chipVars, recalls);
    // One issue total, not one per colliding pair — same reasoning as the equation-mode case above.
    if (chipIssues.length > 0) {
    // ERROR, matching the equation-mode collision above, because it IS the same check: both
    // enumerate every combination of the declared variable domains and flag a pair that matches in
    // any one of them. Neither proves the two are always equal. This was a warning on the theory
    // that a chip collision is "a coincidence of the ranges chosen" while a formula collision is
    // mathematical identity — but `checkOptionFormulaIssues` samples the same finite domain and
    // reports the same kind of coincidence, so the distinction was not real.
    //
    // It had the severity backwards, too. In equation mode a teacher can read two formulas side by
    // side and reason about whether they collide; with chips in plain text the collision is
    // invisible without enumerating rolls mentally. The harder case to spot was the quieter one.
      issues.push({ key: 'option-chip-collision', fields: chipIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.options.optionFormulaCollision') });
    }
  }

  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  const hasMissingFeedbackText = hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) ||
    !!d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states));
  if (hasMissingFeedbackText) issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });

  return issues;
}

// ─── Option row ───────────────────────────────────────────────────────────────

interface SortableOptionRowProps {
  opt: DropdownOption;
  optionCount: number;
  roleLocked: boolean;
  showAnchor: boolean;
  armedOptionId: string | null;
  setArmedOptionId: (id: string | null) => void;
  onRoleClick: (id: string) => void;
  onToggleAnchor: (id: string) => void;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  optionEditorsRef: { current: Map<string, Editor> };
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  /** When set, the content editor is a plain FormulaField (mathjs expression, richtext fully
   * disabled) instead of the usual RichTextEditor — see DropdownBlockData.optionsAsEquations'
   * doc comment. `onFormulaChange`/`formulaHasError`/`variables`/`rolledValues` are only ever
   * consulted in this mode. */
  formulaMode?: boolean;
  onFormulaChange?: (id: string, formula: string) => void;
  formulaHasError?: boolean;
  /**
   * This row is indistinguishable from another one — identical content, or content that resolves
   * the same for some roll of the variables it carries. The panel NAMES the collision; this points
   * at which rows it means. Stays lit while the row has focus: a collision is a relationship
   * between two rows, and you need to see the other one while fixing this one.
   */
  hasDuplicate?: boolean;
  variables?: TaskVariable[];
  rolledValues?: Record<string, string>;
  angleMode?: AngleMode;
}

function SortableOptionRow({ opt, optionCount, roleLocked, showAnchor, armedOptionId, setArmedOptionId, onRoleClick, onToggleAnchor, onTextChange, onRemove, optionEditorsRef, activeEditorRef, setActiveEditor, formulaMode, onFormulaChange, formulaHasError, variables, rolledValues, angleMode , hasDuplicate}: SortableOptionRowProps) {
  /* Warns when this row is the only thing choosing a version somewhere later —
     see `useDrivenRowDependents`. Undefined for the overwhelming majority. */
  const strandWarning = useDrivenRowDeleteWarning(opt.id);
  /* See `useArmSettle`: the warning below is what moves the button. */
  const armSettled = useArmSettle(armedOptionId === opt.id);
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  // Same rule as every other error marker: wait for the row to stop changing before the ring
  // first appears, then hold it until the collision is actually gone.
  const showDuplicate = useDelayedStickyError(!!hasDuplicate, opt.formula ?? opt.text);

  return (
    <div
      data-field-id={opt.id}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className='flex min-w-0 flex-col gap-1'
    >
      <div
        className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
          armedOptionId === opt.id ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
        }`}
      >
        <div
          {...listeners}
          {...attributes}
          aria-label={t('blocks.dropdown.dragOption')}
          className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
        >
          <GripVertical className='h-4 w-4' />
        </div>

        <button
          type='button'
          onClick={() => onRoleClick(opt.id)}
          disabled={roleLocked}
          // Locked outside graded, so it must not invite "set correct" in a mode that has no
          // correct — it names the role it is showing instead, like every other block's cycler.
          aria-label={roleLocked ? t('blocks.roleCycle.neutral')
            : opt.role === 'correct' ? t('blocks.dropdown.markedCorrect') : t('blocks.dropdown.markSetCorrect')}
          className='-mx-3 flex shrink-0 items-center justify-center p-3 disabled:cursor-not-allowed disabled:opacity-40'
        >
          <span className={`pointer-events-none flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
            opt.role === 'correct' ? 'border-2 border-ws-primary bg-ws-primary' :
            opt.role === 'incorrect' ? 'bg-ws-muted/20' :
            'border-2 border-ws-border'
          }`}>
            {opt.role === 'correct' && <Check className='h-3.5 w-3.5 text-ws-btn-on-primary' strokeWidth={3} />}
            {opt.role === 'incorrect' && <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />}
            {opt.role === 'neutral' && <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />}
          </span>
        </button>

        <div className={`min-w-0 flex-1 rounded ${showDuplicate ? 'ring-1 ring-ws-error-fg' : ''}`}>
          {formulaMode ? (
            <FormulaField
              fieldId={`options.${opt.id}.formula`}
              value={opt.formula ?? ''}
              onChange={(formula) => onFormulaChange?.(opt.id, formula)}
              variables={variables ?? []}
              rolledValues={rolledValues ?? {}}
              ariaLabel={t('blocks.options.optionFormulaLabel')}
              placeholder={t('blocks.options.optionFormulaPlaceholder')}
              hideEval
              hasError={formulaHasError}
              angleMode={angleMode}
              onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
            />
          ) : (
            <RichTextEditor fieldId={`${opt.id}.text`}
              content={opt.text}
              onChange={(html) => onTextChange(opt.id, html)}
              placeholder={t('blocks.options.optionPlaceholder')}
              editorClassName='min-h-6'
              hideToolbar
              onEditorReady={(e) => { optionEditorsRef.current.set(opt.id, e); }}
              onEditorFocus={() => {
                const e = optionEditorsRef.current.get(opt.id);
                if (e) { activeEditorRef.current = e; setActiveEditor(e); }
              }}
            />
          )}
        </div>

        {showAnchor && (
          <button
            type='button'
            onClick={() => onToggleAnchor(opt.id)}
            aria-pressed={opt.anchored}
            aria-label={opt.anchored ? t('blocks.options.unpinOption') : t('blocks.options.pinOption')}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors ${
              opt.anchored ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            }`}
          >
            <Pin className='h-4 w-4' fill={opt.anchored ? 'currentColor' : 'none'} />
          </button>
        )}

        <IconButton
          type='button'
          variant='destructive'
          size='md'
          isArmed={armedOptionId === opt.id}
          disabled={optionCount <= 1}
          /*
             SAID TWICE, ON PURPOSE: in the armed label for a reader, and in the warning
             beside the button for everyone else. It used to be the label alone, which made
             it invisible — see `StrandWarning` for why that was chosen and why it was
             wrong. `title` stays for a pointer resting on the icon itself. */
          aria-label={armedOptionId === opt.id
            ? [t('blocks.options.confirmRemoveOption'), strandWarning].filter(Boolean).join('. ')
            : t('blocks.options.removeOption')}
          title={armedOptionId === opt.id ? strandWarning : undefined}
          className='shrink-0'
          onClick={() => {
            if (armedOptionId === opt.id) { onRemove(opt.id); setArmedOptionId(null); }
            else { setArmedOptionId(opt.id); }
          }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedOptionId === opt.id && armSettled()) setArmedOptionId(null); }}
          onBlur={() => { if (armedOptionId === opt.id) setArmedOptionId(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && armedOptionId === opt.id) { e.stopPropagation(); setArmedOptionId(null); } }}
        >
          <Trash2 className='h-4 w-4' />
        </IconButton>
      </div>

      {/*
        * BELOW THE ROW, not inside it. Beside the fields it squeezed the answer to make room
        * for a squeezed warning, which helps nobody — owner: "squishing the actual answer to
        * put a squished warning is not helpful". The wrapper carries the sortable ref and the
        * field id so the row keeps being draggable and reachable from the issue list; the
        * armed paint stays on the row, which is the thing being deleted.
        */}
      {armedOptionId === opt.id && strandWarning && <StrandWarning text={strandWarning} />}
    </div>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────

interface TriggerOption {
  value: string;
  label: string;
  preview?: string;
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}


// ─── Rich-content answer picker ────────────────────────────────────────────────
// A native <select>/<option> can only ever show plain text — every option gets crushed through
// stripHtmlForLabel, which is exactly what turns a reaction like `2H2 + O2 -> 2H2O` or a formula
// like `y = mx + b` into an unreadable wall of escaped/plain characters. This is a from-scratch
// button+listbox combobox instead, so options can render their real rich HTML (rendered KaTeX/
// reaction markup included) — same accessible shape as DropdownTriggerDropdown above (button
// opens a listbox, arrow keys move focus between role='option' rows, Escape closes and refocuses
// the button), just without that component's evaluation-color chip, since this is the plain
// student-facing answer control, not a feedback-authoring trigger picker. `ws-formula-preview`
// (already used elsewhere in this file for exactly this content) disables pointer-events on the
// rendered formula/reaction nodes so a click anywhere on the option still registers as a select.
interface RichSelectOption { id: string; html: string }

// A blank option still needs a selectable, tappable row in this real interactive control (unlike
// the read-only summary/legend displays elsewhere, it can't just show nothing) — but it must not
// look like real content either, so it renders a lone `&nbsp;` instead of a positional "Option N"
// placeholder: the row stays open and clickable at its normal height, with nothing visibly wrong
// on top, keeping the "you haven't written this option yet" signal squarely in the block's own
// error reporting rather than manufacturing a second, misleading one here.
function richSelectOptionsFromDropdownOptions(
  options: DropdownOption[], rolledValues: Record<string, string>, chipScope?: ChipScope,
): RichSelectOption[] {
  return options.map(o => ({
    id: o.id,
    // RESOLVED, not chipified — this is the answer control a student operates, so a variable has
    // to read as the value they are being asked about. The feedback panel is the surface that
    // shows chips, because it names a condition rather than an instance of one. Where no roll has
    // happened yet, processVariableChipHTML falls back to a display chip rather than the bare
    // name it used to leave behind.
    html: processVariableChipHTML(o.text, rolledValues, chipScope).replace(/<\/?p>/g, '').trim() || '&nbsp;',
  }));
}

function RichOptionSelect({ options, value, onChange, placeholder, disabled, className }: {
  options: RichSelectOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const optionBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = options.find(o => o.id === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const selectedIdx = options.findIndex(o => o.id === value);
    const focusIdx = selectedIdx >= 0 ? selectedIdx + 1 : 0;
    const id = requestAnimationFrame(() => { optionBtnRefs.current[focusIdx]?.focus(); });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Down opens it, which it did not.
   *
   * There was no keydown handler on the trigger at all — `handleListboxKeyDown` lives on the popup
   * and only runs once it is already open. So a keyboard user who has just been told "combo box",
   * and therefore knows Down opens one, pressed Down and got nothing. The role cannot be made
   * honest without this.
   *
   * Enter and Space are deliberately absent: a native `<button>` already activates on both, and
   * intercepting them here would mean re-implementing what the browser does correctly.
   */
  function handleTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (open || e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    setOpen(true);
  }

  function handleListboxKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
    const refs = optionBtnRefs.current.filter((r): r is HTMLButtonElement => r !== null);
    const currentIdx = refs.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); refs[Math.min(currentIdx + 1, refs.length - 1)]?.focus(); break;
      case 'ArrowUp':   e.preventDefault(); refs[Math.max(currentIdx - 1, 0)]?.focus(); break;
      case 'Home':      e.preventDefault(); refs[0]?.focus(); break;
      case 'End':       e.preventDefault(); refs[refs.length - 1]?.focus(); break;
      case 'Escape':    e.preventDefault(); setOpen(false); btnRef.current?.focus(); break;
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative min-w-0 ${className ?? ''}`}
      onBlur={(e) => { if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
    >
      <button
        ref={btnRef}
        type='button'
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        onKeyDown={handleTriggerKeyDown}
        /**
         * `combobox`, not a bare button. A button promises "something happens when you press
         * this"; a combobox promises "you are choosing among options, and what you just heard is
         * the current one" — and since the name is now the selected option, a listener otherwise
         * had no way to tell their own answer from a label on a button.
         *
         * Focus really moves into the option buttons rather than riding `aria-activedescendant`.
         * ARIA's listbox pattern permits that as an alternative, so the popup needs no change; what
         * it does mean is that `aria-controls` is the only thing tying the two together, and the
         * keyboard has to keep the promise the role makes — see `handleTriggerKeyDown`.
         */
        role='combobox'
        aria-haspopup='listbox'
        aria-controls={open ? listboxId : undefined}
        aria-expanded={open}
        /**
         * No `aria-label`. It used to carry the placeholder, and an `aria-label` REPLACES an
         * element's content as its name — so this button announced "Select an option" forever.
         * A student who picked 10 m/s and tabbed back heard "Select an option, button" and had no
         * way to hear what they had chosen, or that they had chosen at all.
         *
         * The content below is already the right name: the selected option, or the placeholder
         * when there is none. Letting it name the button means the name tracks the selection for
         * free, which is the whole reason a name is computed from content rather than stored.
         *
         * The chevron is `aria-hidden`, so nothing else leaks in. Still missing
         * `role='combobox'` — this is a button that owns a popup, which is a weaker promise than
         * the widget actually keeps. Deliberately separate: that one changes the interaction
         * contract (it moves real focus into the option buttons rather than using
         * `aria-activedescendant`) and wants its own tests.
         */
        // min-h (not a fixed h) — a comfortable baseline that absorbs a typical equation
        // option's extra vertical extent (fractions, sub/superscripts) without growing past it,
        // so selecting one doesn't visibly push everything below the dropdown further down than
        // plain-text options already do; a genuinely tall equation can still grow it further.
        className='flex min-h-12 w-full min-w-0 items-center gap-2 rounded-md border border-ws-border bg-transparent px-3 py-2 text-sm text-ws-text focus:border-ws-primary focus:outline-none focus-visible:[outline:2px_auto] disabled:cursor-not-allowed disabled:text-ws-muted'
      >
        {current ? (
          <span className='min-w-0 flex-1 truncate text-left ws-formula-preview' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(current.html) }} />
        ) : (
          <span className='min-w-0 flex-1 truncate text-left text-ws-muted'>{placeholder}</span>
        )}
        <ChevronDown aria-hidden='true' className={`h-4 w-4 shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <ul
          id={listboxId}
          role='listbox'
          // Not the placeholder: the trigger is now named by the placeholder-or-selection, and
          // giving the list the same string made the two announce identically, so opening the
          // list sounded like nothing had happened.
          aria-label={t('blocks.dropdown.optionsListLabel')}
          onKeyDown={handleListboxKeyDown}
          className='absolute left-0 top-full z-20 mt-1 w-full max-h-60 overflow-y-auto overscroll-contain rounded-lg border border-ws-border bg-ws-elevated py-1 shadow-lg'
        >
          <li>
            <button
              ref={(el) => { optionBtnRefs.current[0] = el; }}
              type='button'
              role='option'
              aria-selected={value === null}
              tabIndex={-1}
              onClick={() => { onChange(null); setOpen(false); btnRef.current?.focus(); }}
              className={`flex w-full items-center px-3 py-2 text-left text-sm italic text-ws-muted hover:bg-ws-hover focus:bg-ws-hover focus:outline-none ${value === null ? 'font-medium' : ''}`}
            >
              {placeholder}
            </button>
          </li>
          {options.map((opt, i) => (
            <li key={opt.id}>
              <button
                ref={(el) => { optionBtnRefs.current[i + 1] = el; }}
                type='button'
                role='option'
                aria-selected={opt.id === value}
                tabIndex={-1}
                onClick={() => { onChange(opt.id); setOpen(false); btnRef.current?.focus(); }}
                className={`flex w-full min-w-0 items-center px-3 py-2 text-left text-sm hover:bg-ws-hover focus:bg-ws-hover focus:outline-none ${opt.id === value ? 'font-medium text-ws-primary' : 'text-ws-text'}`}
              >
                <span className='min-w-0 flex-1 ws-formula-preview' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(opt.html) }} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DropdownTriggerChip({ option }: { option: TriggerOption }) {
  const chipScope = useChipScope();
  const chipColor =
    chipToneClass(option.evaluation ?? 'neutral');
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {option.evaluation === 'incorrect' && <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {option.evaluation === 'correct'   && <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {(option.evaluation === 'neutral' || !option.evaluation) && <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
      <ChipDivider />
      {/* Same rule as DropdownTriggerDropdown's own selected-button display above: once an
          option is chosen, show its real text or nothing — never the positional "Option N"
          label, which belongs to the picker menu only. */}
      {option.preview && (
        <span className={`${TRIGGER_CHIP_TEXT_CLASS} ws-formula-preview`} dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(renderChipsForDisplay(option.preview.replace(/<\/?p>/g, '').trim(), chipScope)) }} />
      )}
    </span>
  );
}

// ─── Test panel derivation ──────────────────────────────────────────────────
// Pulled out of the collapsed/isSelected render path so the SAME derivation can build a live
// test panel from two different data sources: `d` from `initData(block.data)` (the desktop
// collapsed card — a separate mount from the editor, only ever reading persisted props) and a
// `d`-shaped object built from the editor's own live local state (the phone full-screen editor's
// own "Test" tab — see its own construction below). Neither the interactive test STATE
// (rolledValues/testSelectedId/submittedSelectedId/hasSubmitted — all plain top-level `useState`
// in the component, never gated behind `isEditing`) nor the handlers that mutate it need
// duplicating; only this derivation (which used to read `d` from a closure only the collapsed
// branch had) did.
interface DropdownTestData {
  resolvedVariables: Record<string, number>;
  effectiveOptions: DropdownOption[];
  matchedBranch: ConditionalRoleBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
  testResult: FeedbackState[] | null;
  resolvedD: DropdownBlockData;
  testChips: TestBarChip[];
  hasChips: boolean;
  hasRollable: boolean;
}

/** Backfills default feedback text/titles for a block read straight from `block.data` (which a
 * brand-new block, or one saved before a given default existed, may not have) — shared by both
 * non-editing render paths (the collapsed/isSelected test widget and the student-facing Preview)
 * so they can never drift into two different sets of defaults. Pulled out rather than duplicated
 * inline, matching computeDropdownTestData's own "one derivation, multiple data sources" reasoning
 * right above. */
/**
 * Normalised block data, with the drawn question VERSION already in force.
 *
 * Version first, then normalise: a version carries its own feedback, and it has to go
 * through the same defaulting the block's own does or a versioned block would miss the
 * titles a plain one gets. `applyOptionsVersion` is identity when there are no versions, so
 * this is unchanged for every block that has never had one authored.
 */
function buildNormalizedDropdownData(
  rawD: DropdownBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, from the session roll. */
  versionId?: string,
): DropdownBlockData {
  const rawVersioned = applyOptionsVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeFeedback(
      rawVersioned.feedback,
      {
        unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
        unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
        incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
        incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
        correctTitle: rawVersioned.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
          : rawVersioned.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
          : t('blocks.feedbackDefaults.anyResponseTitle'),
        correctContent: rawVersioned.gradingMode === 'graded' ? `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`
          : rawVersioned.gradingMode === 'survey' ? `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`
          : `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
      },
      [],
      positiveTriggerForMode(rawVersioned.gradingMode),
    ),
  };
}

// Fired-feedback display — Preview and the live test widget both show this identically once an
// answer has been submitted (a per-block Submit in the test widget, Preview's own single
// page-level Submit), so it's shared rather than duplicated the way it briefly was. Resolves
// variable chips in the feedback content against the same rolled values the question/options
// already display with — a chip in feedback text is authored the same way as one in a
// question/option, so it needs the same substitution before render, not the raw `{var}` markup.
//
// `default` states (No Answer/Correct/Incorrect/any_answer/any_response) only ever fire alone —
// `evaluateDropdown` returns the `correct` state in total isolation, terminally, before it ever
// considers targeted states — so they keep their existing one-box-per-state look untouched.
// `targeted` (targeted) states are structurally never `evaluation: 'correct'` (locked to
// `['neutral', 'incorrect']` in the editor, same as MCQ's own `renderMCQFiredFeedback`), so
// grouping them into an incorrect and a neutral cluster is exhaustive. Dropdown is single-select
// so at most one targeted state ever fires at once in practice — this still routes through the
// same clustering shape as MCQ (whose multi-select can fire several in parallel) for consistency,
// and a single-message cluster renders identically to the old one-box-per-state look anyway.
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderDropdownFiredFeedback(testResult: FeedbackState[] | null, rolledValues: Record<string, string>): React.ReactNode {
  return <FiredFeedback states={testResult} rolledValues={rolledValues} />;
}

// The one canonical "what a student sees" layout — question, then the answer control — shared by
// every place that renders a student-facing view of this block (currently the teacher-facing
// Preview and the editor's own live-test widget; the eventual student interface should reuse this
// same shape once it consumes @agavi/blocks, rather than re-deriving the layout independently).
// Before this existed, Preview and the test widget each hand-wrote the same question+select JSX,
// and had already drifted (Preview was missing the test widget's `mx-auto` centering). Deliberately
// does NOT own any outer card/border chrome — whether a faint outline is needed to keep blocks
// visually distinct in student view/Preview is still an open design question, so that decision
// stays entirely with whatever wraps this, not baked in here. `children` is the fired-feedback
// display (see renderDropdownFiredFeedback) — not part of the canonical shape itself, since a
// genuinely different context (e.g. a future read-only "review" mode) might want something else
// there entirely.
function DropdownAnswerDisplay({
  question, options, optionOrder, rolledValues, value, onChange, placeholder, disabled, selectClassName, scenario, children,
}: {
  question: string;
  /** The follower's stem, ghosted, above the question — see `AnswerDisplayFrame`. Passed by
   *  the editor's test surfaces only; Preview has the leader block itself on the page. */
  scenario?: React.ReactNode;
  options: DropdownOption[];
  optionOrder: string[] | null;
  rolledValues: Record<string, string>;
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
  disabled?: boolean;
  selectClassName?: string;
  children?: React.ReactNode;
}) {
  const chipScope = useChipScope();
  return (
    <AnswerDisplayFrame question={question} rolledValues={rolledValues} scenario={scenario}>
      <RichOptionSelect
        options={richSelectOptionsFromDropdownOptions(reorderByIds(options, optionOrder), rolledValues, chipScope)}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={selectClassName ?? 'mx-auto w-full md:max-w-xs'}
      />
      {children}
    </AnswerDisplayFrame>
  );
}

function computeDropdownTestData(
  d: DropdownBlockData,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submittedSelectedId: string | null,
  hasSubmitted: boolean,
  locale: string,
  /**
   * The leader variant in force, when this block follows one.
   *
   * Passed in rather than derived: the three callers below know it from three different places —
   * the task-wide preview roll, the editor's own pager, the phone test tab — and a function that
   * went looking for it would have to know about all three.
   */
  scenarioId?: string,
): DropdownTestData {
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  // Branches are only ever consulted in graded mode (mirrors evaluateDropdown's own gate — see its
  // doc comment) — otherwise a leftover branch from a mode switch would still resolve here even
  // though the real evaluation now ignores it, showing a "Testing Condition" banner and a
  // branch-scoped chip legend for a mode that's actually running on the plain top-level feedback.
  // A follower resolves against the scenario in force instead of an equation — the same split
  // `evaluateDropdown` makes internally, mirrored here because this builds what the teacher SEES
  // and the two must never disagree about which answer key is live.
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch(d.scenarioBranches!, scenarioId) : null;
  const effectiveOptions = rolesForMode(d.gradingMode, d.gradingMode !== 'graded'
    ? d.options
    : followed
      /* The same arguments the evaluator passes. These two editors do not offer nested
         branching yet, but `evaluateDropdown`/`evaluateKeyword` already resolve it, and an
         editor that omitted them would show a different answer key than the one that grades
         the moment a row carried one. See MCQ, where exactly that shipped. */
      ? resolveScenarioOptions(d.options, d.scenarioBranches, scenarioId, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
      : resolveEffectiveOptions(d.options, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation')));
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  const activeFeedbackForDisplay = scenarioRow ? scenarioRow.feedback : matchedBranch ? matchedBranch.feedback : d.feedback;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  const testResult = hasSubmitted ? evaluateDropdown({ selectedId: submittedSelectedId }, d, resolvedVariables, scenarioId) : null;
  const resolvedD: DropdownBlockData = {
    ...d,
    options: effectiveOptions.map(o => ({
      ...o,
      text: d.optionsAsEquations
        ? dropdownOptionDisplayText(o, true, resolvedVariables, locale, angleModeFor(d, `options.${o.id}.formula`))
        : processVariableChipHTML(o.text, rolledValues),
    })),
  };
  // One answer for both surfaces, built from the branch-resolved options so it describes the setup
  // a teacher is looking at rather than the static shape underneath.
  const barReach = dropdownReachability(d, effectiveOptions);
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const isVisibleTargeted = (s: FeedbackState) => {
    if (s.type !== 'targeted') return false;
    if (!s.trigger.startsWith('option_')) return true;
    const id = s.trigger.slice('option_'.length);
    return effectiveOptions.find(o => o.id === id)?.role !== 'correct';
  };
  const { listed: legendStates, catchAll: participationCatchAll } =
    legendStatesFor(d.gradingMode, activeFeedbackForDisplay.states, isVisibleTargeted);
  const testChips: TestBarChip[] = legendStates.map(s => {
    const fired = firedIds.has(s.id);
    return {
      id: s.id,
      condition: dropdownStateCondition(s, fired ? submittedSelectedId : null, resolvedD, t),
      evaluation: displayEvaluation(d.gradingMode, s.evaluation),
      active: fired,
      category: s.trigger === 'unanswered' ? 'unanswered' as const
        : s.trigger === 'correct' ? 'correct' as const
        : s.trigger === 'any_answer' ? 'any_answer' as const
        : s.trigger === 'any_response' ? 'any_response' as const
        : 'targeted' as const,
    };
  });
  const correctState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'correct') : undefined;
  if (correctState) {
    testChips.push(...toTestBarChips(dropdownChips('correct', barReach, opt => dropdownOptionNode(resolvedD.options.find(o => o.id === opt.id) ?? opt, d.optionsAsEquations)), {
      id: correctState.id,
      category: 'correct',
      active: firedIds.has(correctState.id),
      activeSourceIds: new Set(submittedSelectedId ? [submittedSelectedId] : []),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  }
  const incorrectAnyState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'incorrect') : undefined;
  if (incorrectAnyState) {
    testChips.push(...toTestBarChips(
      dropdownChips('incorrect', barReach, opt => dropdownOptionNode(resolvedD.options.find(o => o.id === opt.id) ?? opt, d.optionsAsEquations)), {
        id: incorrectAnyState.id,
        category: 'incorrect',
        active: firedIds.has(incorrectAnyState.id),
        // "or" between them, matching the panel's own row: any one of these lands here, they are
        // not a set of requirements. Only the option actually picked lights up.
        activeSourceIds: new Set(submittedSelectedId ? [submittedSelectedId] : []),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
  }

  /**
   * The catch-all, fanned out exactly as graded's Default Incorrect is just above.
   *
   * It used to arrive through `legendStates` as ONE chip reading "Any Answer", which is the single
   * thing the feedback panel stopped saying when the catch-all learned to name what it still
   * catches. Same derivation as the panel's now, so the two cannot word it differently.
   */
  if (participationCatchAll) {
    testChips.push(...toTestBarChips(
      catchAllChips(d.gradingMode, legendStates.some(s => s.type === 'targeted'), t,
        () => dropdownChips('incorrect', barReach, opt => dropdownOptionNode(resolvedD.options.find(o => o.id === opt.id) ?? opt, d.optionsAsEquations), d.gradingMode)), {
        id: participationCatchAll.id,
        category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
        active: firedIds.has(participationCatchAll.id),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
  }
  const hasChips = htmlHasVariableChips(d.question) || (d.optionsAsEquations
    ? d.options.some(o => getEquationVariableNames(o.formula ?? '').length > 0)
    : d.options.some(o => htmlHasVariableChips(o.text)));
  const hasRollable = hasRollableVariables(variables);
  return { resolvedVariables, effectiveOptions, matchedBranch, matchedBranchIndex, matchedEquationValue, testResult, resolvedD, testChips, hasChips, hasRollable };
}

// Module-level (not per-instance) — see mcq-block.tsx's identical caches for why: dropdownIssues
// shares the same expensive checkOptionChipCollisions brute-force check MCQ does, and re-ran on
// every keystroke anywhere in the task, not just this block's own fields, before this existed.
const dropdownIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();
const dropdownTestDataCache: IssueCache<DropdownTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft.
 *
 * Preview's chips describe the SUBMITTED answer, because that is what the student has been told
 * about. Behind the Scenes wants the other question -- what would fire if they pressed Submit
 * now -- and the two part company the moment someone edits after submitting. Keyed on the draft,
 * so it costs one derivation per distinct answer tried rather than one per keystroke.
 */
const dropdownDraftChipsCache: IssueCache<DropdownTestData> = new WeakMap();

// ─── Main component ───────────────────────────────────────────────────────────

export default function DropdownBlock({ block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, blocks, onRename }: DropdownBlockProps) {
  const { t, i18n } = useTranslation();
  /* The top rung, asked once for the whole editor. It gates only whether a mechanism may be
     ADOPTED; each toggle below keeps its own `|| <already on>` so one in use can be switched
     off. See `useVariableMechanismAllowed` for why the insert gate does not imply this. */
  const mayVarMechanism = useVariableMechanismAllowed();
  /*
   * MAY A RECALL LIVE IN THIS BLOCK — the destination half of the recall rung.
   *
   * The same question the toolbar's `[` button asks, asked again here because the equations
   * toggle is a MECHANISM and a mechanism gate is never implied by an insert gate: a chip
   * arrives by paste and by a rung lowered after authoring, and either would otherwise open a
   * control the capability does not grant.
   */
  const mayRecallHere = useRecallAffordance() === 'allowed';
  const scope = useAnswerRefScope();
  /* Which `[ara_…]` tokens may become live chips again when the toggle goes OFF — an id naming
     no alias is a reference to nothing, and must stay text. Every alias, not just the numeric
     ones: a token that should never have been in a formula is HEALED by turning the toggle off,
     and rebuilding the chip is the repair. */
  const knownAliasIds = useMemo(
    () => new Set((scope?.aliases ?? []).map(a => a.id)), [scope]);
  /*
   * AND WHICH OF THEM A FORMULA CAN ACTUALLY USE.
   *
   * A calculated option is arithmetic. `aliasKind` is the one derivation of "number or words",
   * the same one the recall panel's `#`/`A` letter reads, so the toggle and the field agree
   * about which recalls belong in an equation instead of each deciding for itself.
   */
  const numericAliasIds = useMemo(
    () => new Set((scope?.aliases ?? [])
      .filter(a => aliasKind(a, (scope?.tasks ?? []) as Parameters<typeof aliasKind>[1]) === 'number')
      .map(a => a.id)),
    [scope]);
  const isPhone = useIsPhone();
  const chipScope = useChipScope();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const placeholderDefault = t('blocks.dropdown.placeholderDefault');
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Every `useState` below seeds from this, so it has to be the VERSION's content, not the
   *  block's vestigial base copy — see mcq-block.tsx's identical note. */
  const initial = useRef(applyOptionsVersion(initData(block.data, placeholderDefault), editorVersion));
  const shownVersions = (initData(block.data, placeholderDefault).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read `push()` depends on; a ref cannot re-render, so restored HTML needs state to
   * reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedOptions, setOptions] = useState<DropdownOption[]>(initial.current.options);
  const [editedRandomizeOrder, setRandomizeOrder] = useState(initial.current.randomizeOrder);
  const [editedPlaceholder, setPlaceholder] = useState(initial.current.placeholder);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalRoleBranch[] | undefined>(initial.current.conditionalBranches);
  // Unchecking "Correctness depends on variables" clears the equation/branches from state — but a
  // teacher who rechecks it before closing the editor shouldn't lose what they'd typed. Holds the
  // last-known equation/branches across an off→on cycle within the same editing session; cleared
  // for real in handleDone, so it never survives an exit while the toggle is left off.
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle. Dropped on close, and that is
   * what makes the removal permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<DropdownBlockData['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);
  /**
   * WHAT LINKING AND UNLINKING PUT ASIDE FOR EACH OTHER, for as long as the editor stays open.
   *
   * The two acts are destructive in opposite directions — linking clears the equation and its
   * branches (the block's equation cannot select among scenarios), unlinking drops the rows — and
   * before this each threw the other's work away for good. A teacher who unlinked to look at
   * something and relinked had every scenario's answer back at its copied default.
   *
   * Same contract as `savedConditionalDraftRef`, deliberately: recoverable while you are still
   * looking at the block, dropped on close, and after that recovery is undo. A REF and not a
   * stored field, which is the one difference — the branch draft persists so it can survive a
   * reload, and nothing has asked that of this yet.
   *
   * `leaderId` is carried with the rows because rows are keyed by the LEADER's variant ids: give
   * them back against a different leader and `syncScenarioBranches` would match nothing anyway,
   * so the check makes the intent explicit rather than relying on that.
   */
  const linkSwapRef = useRef<{
    scenario?: { leaderId: string; rows: ScenarioRoleBranch<DropdownFeedback>[] | undefined };
    numeric?: { equation: string | undefined; branches: ConditionalRoleBranch[] | undefined };
  }>({});
  // "Treat options as equations" — see DropdownBlockData.optionsAsEquations' doc comment for the
  // full contract (formula evaluated for display, correctness stays 100% manual). Toggling this
  // off doesn't touch any option's `formula` field — it's preserved so re-checking it doesn't lose
  // anything, for as long as this editor session stays open (see the editor-close pruning below).
  const [editedOptionsAsEquations, setOptionsAsEquations] = useState(initial.current.optionsAsEquations ?? false);
  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read an issue list built from `block.data`, the editor panel reads one
  // built from live state, and the two are often rendered by different instances of this
  // component — so each must be gated by the settling of the value it was computed from.
  // Degrees/radians disambiguation for any trig call in either conditionalEquation or an option's
  // formula — one shared mode for the whole block, not per-formula (mirrors optionsAsEquations'
  // own block-level scoping, and Numerical's own angleMode field). See DropdownBlockData.angleMode.
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');
  /**
   * One answer setup per scenario the leader can show — see `docs/linked-scenarios.md` §4.1.
   *
   * Local state like every other edited field, rather than written straight to stored data the way
   * `followsBlockId` is. The two are not the same kind of thing: linking is a single structural act,
   * while these rows carry roles and feedback that the ordinary editing surfaces write to on every
   * click and keystroke. Holding them here is what lets the scenario row reuse `activeBranch`'s
   * existing read/write path verbatim instead of growing a parallel one, which is the drift this
   * feature exists to prevent.
   */
  const [editedScenarioBranches, setScenarioBranches] = useState<ScenarioRoleBranch<DropdownFeedback>[] | undefined>(initial.current.scenarioBranches);
  /**
   * WHICH answer setup is on screen — shared by the numeric and scenario pagers alike.
   *
   * Deliberately one pointer, not one each. A block answers to a scenario or to an equation, never
   * both at BLOCK level, so only one such pager is mounted and a second index would be dead state that
   * `requestBranchJump` would then have to choose between.
   */
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, options: editedOptions, randomizeOrder: editedRandomizeOrder,
    placeholder: editedPlaceholder, gradingMode: editedGradingMode,
    conditionalEquation: editedConditionalEquation, conditionalBranches: editedConditionalBranches,
    optionsAsEquations: editedOptionsAsEquations, angleMode: editedAngleMode,
    scenarioBranches: editedScenarioBranches,
  };
  const restored = useRestoredData<DropdownBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyOptionsVersion(initData(raw as Parameters<typeof initData>[0], placeholderDefault), editorVersion),
    current: { ...applyOptionsVersion(initData(block.data, placeholderDefault), editorVersion), ...edited } as DropdownBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — shared by an undo, another
   *  device's save, and switching version, which are the same act from here. */
  function seedEditorFrom(next: DropdownBlockData) {
      setOptions(next.options);
      setRandomizeOrder(next.randomizeOrder);
      setPlaceholder(next.placeholder);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setOptionsAsEquations(next.optionsAsEquations ?? false);
      setAngleMode(next.angleMode ?? 'degrees');
      setScenarioBranches(next.scenarioBranches);
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyOptionsVersion(initData(block.data, placeholderDefault), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data, placeholderDefault)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyOptionsVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const options = isEditing ? editedOptions : restored.data.options;
  const randomizeOrder = isEditing ? editedRandomizeOrder : restored.data.randomizeOrder;
  const placeholder = isEditing ? editedPlaceholder : restored.data.placeholder;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const optionsAsEquations = isEditing ? editedOptionsAsEquations : (restored.data.optionsAsEquations ?? false);
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');
  const scenarioBranches = isEditing ? editedScenarioBranches : restored.data.scenarioBranches;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * EVERY field write goes through here, because `onUpdate` REPLACES `block.data` wholesale.
   *
   * `handleUpdateBlock` does `blocks.map(b => b.id === id ? { ...b, data } : b)`, so a write that
   * builds its object field by field deletes everything it forgot to name. In MCQ two of them
   * forgot `versions`, and the one in the feedback model's `onUpdate` is reached by
   * `seedEditorFrom` — which is what switching version calls — so opening a version destroyed the
   * whole bank. Taking a PARTIAL is the point: a caller can only state what it is changing.
   */
  const emitFields = (next: Partial<DropdownBlockData>) => {
    const base = initData(block.data, placeholderDefault);
    emit(writeOptionsVersion(
      base, editorVersion, { ...base, ...next } as DropdownBlockData,
    ) as unknown as Record<string, unknown>);
  };

  const storedData = initData(block.data, placeholderDefault);

  /**
   * Follow a leader, or stop following one.
   *
   * The rows are a PROJECTION of the leader's variants (§4.1), so this never asks the teacher to
   * build a list — it reads the leader's bank and writes one row per variant, each a copy of the
   * answer setup the block already had, marked unreviewed because a copy is not a decision.
   *
   * Linking also clears the NUMERIC branch setup. A scenario and an equation are two selectors
   * and the block's own cannot select among scenarios. Leaving it behind would mean a block whose
   * answer key depends on which of two systems you looked at.
   */
  /**
   * A bank and a link cannot coexist (§2.2), so linking a banked block asks first — §7.1.
   *
   * Held as the pending leader id rather than a boolean: the answer arrives from the modal,
   * and by then the menu has closed.
   */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data, placeholderDefault);
    // A bank is the one case that needs a decision before anything is written.
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      // Unlinking keeps the rows' work nowhere to go, so it drops them — and says so, which is
      // the guard's job rather than this one's. For now the data change is the honest minimum.
      // Put the rows aside, and hand back whatever linking took.
      if (current.followsBlockId) {
        linkSwapRef.current.scenario = { leaderId: current.followsBlockId, rows: current.scenarioBranches };
      }
      const restored = linkSwapRef.current.numeric;
      linkSwapRef.current.numeric = undefined;
      setScenarioBranches(undefined);
      setConditionalEquation(restored?.equation);
      setConditionalBranches(restored?.branches);
      emit(versionStructure(block.data, {
        ...current,
        followsBlockId: undefined,
        scenarioBranches: undefined,
        conditionalEquation: restored?.equation,
        conditionalBranches: restored?.branches,
      }));
      return;
    }
    const leader = (blocks ?? []).find(b => b.id === followsBlockId);
    const leaderVersions = (leader?.data as { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /*
     * COLLAPSE THE BANK TO VERSION 1 FIRST, whichever way the teacher answered.
     *
     * A block can hold one question, and once it follows a scenario there is no bank to pick
     * from, so SOMETHING has to become that question. It used to be `initData`'s own copy,
     * which is the vestigial one `writeOptionsVersion` freezes when the first version is made
     * — usually version 1's text, but only by accident of how the bank was built, and silently
     * whatever happened to be there otherwise.
     *
     * Saying "version 1" and meaning it is what lets the modal state the consequence plainly,
     * and it is what makes the two answers differ in exactly one thing: the ANSWERS. The
     * question is the same either way.
     */
    const base = (current.versions?.length ?? 0) > 1
      ? applyOptionsVersion(current, current.versions![0]!.id)
      : current;
    // Whatever this is about to clear, kept for an unlink.
    if (current.conditionalEquation !== undefined || current.conditionalBranches !== undefined) {
      linkSwapRef.current.numeric = {
        equation: current.conditionalEquation, branches: current.conditionalBranches,
      };
    }
    const banked = linkSwapRef.current.scenario;
    const leaderBankNow = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /*
     * REMAP: the block's own versions become its scenario rows, paired by position. Every row
     * comes back unreviewed, which is what makes a guess safe to offer — see
     * `remapVersionsToScenarios`.
     */
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<RoleVersion<DropdownFeedback>, ScenarioRoleBranch<DropdownFeedback>>(
        leaderBankNow, current.versions,
        v => ({ roles: v.roles, feedback: v.feedback }),
        () => ({ roles: Object.fromEntries(base.options.map(o => [o.id, o.role])), feedback: base.feedback }),
      )
      : syncScenarioBranches<ScenarioRoleBranch<DropdownFeedback>>(
        leaderVersions,
        // The rows this block already has, or the ones an unlink put aside for this leader.
        current.scenarioBranches ?? (banked?.leaderId === followsBlockId ? banked.rows : undefined),
      /*
       * Every new row starts as what this block already said, so a teacher edits the ones that
       * differ instead of authoring every scenario from nothing.
       *
       * THE EDITOR'S `feedback`, NOT `current.feedback`. `current` is `initData(block.data)` —
       * the raw stored object — and a block whose feedback has never been written back still
       * holds `{ states: [] }` there, because the No Answer/Correct/Incorrect defaults are
       * produced by `useInputBlockFeedback`'s normalization on mount and only reach storage on
       * the next save. Link a brand-new dropdown before touching anything else and every
       * scenario was born with no feedback at all, copied faithfully from an empty source.
       *
       * The same gap `buildNormalizedDropdownData` exists for on the card side.
       */
        () => ({
          roles: Object.fromEntries(base.options.map(o => [o.id, o.role])),
          feedback,
        }),
      );
    /*
     * A row with no feedback gets the block's, whatever produced it.
     *
     * The template above only runs for rows being CREATED. Two other roads reach this line with
     * an empty one: a row banked by an unlink before the fix above existed, which the recovery
     * bank would hand straight back, and any row written by an earlier build. Healing at the
     * write rather than only at the source is what makes relinking a repair instead of a
     * faithful restoration of the bug.
     */
    const healed = branches.map(b => (b.feedback?.states?.length ? b : { ...b, feedback }));
    // Local state too, or the editor would render from `editedScenarioBranches` — still empty —
    // while the saved block already held the rows.
    setScenarioBranches(healed);
    setConditionalEquation(undefined);
    setConditionalBranches(undefined);
    emit(versionStructure(block.data, {
      // `base`, not `current`: version 1's question and roles become the block's, which is what
      // the modal promised and what makes the bank's collapse legible.
      ...base,
      // The editor's normalized feedback, for the reason the rows have it — `initData`'s copy
      // may never have been written back. This is the fallback `activeFeedbackForBranch`
      // reaches for when no row resolves.
      feedback: remap === 'remap' ? (base.feedback ?? feedback) : feedback,
      followsBlockId,
      scenarioBranches: healed,
      // The bank cannot survive the link (§2.2) — remapped into the rows, or discarded on the
      // teacher's say-so. Either way the decision was made above, never here.
      versions: undefined,
      conditionalEquation: undefined,
      conditionalBranches: undefined,
    }));
  }

  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation, answers: optionsAsEquations ? options.map(o => o.formula ?? '').join('\u0000') : undefined },
    { equation: storedData.conditionalEquation, answers: storedData.optionsAsEquations ? storedData.options.map(o => o.formula ?? '').join('\u0000') : undefined },
  );

  const currentDataRef = useRef({ options, randomizeOrder, placeholder, gradingMode, conditionalEquation, conditionalBranches });
  currentDataRef.current = { options, randomizeOrder, placeholder, gradingMode, conditionalEquation, conditionalBranches };

  // The positive-slot's three known default bodies — "that is the correct answer" doesn't make
  // sense in survey/optional mode, where there's no such thing as correct, only participation or
  // acceptance. Compared by exact string match (via retriggerPositiveSlot) to swap between them
  // on a mode switch without ever touching a teacher's own custom wording.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const { feedback, setFeedback, resyncFeedback, getTitle } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
    },
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const { options, randomizeOrder, placeholder, gradingMode, conditionalEquation, conditionalBranches } = currentDataRef.current;
      emitFields({ question: questionHtmlRef.current, options, randomizeOrder, placeholder, gradingMode, feedback: nextFeedback, conditionalEquation, conditionalBranches });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;
  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write below that deals with feedback CONTENT (No Answer/Correct/Incorrect/
  // Targeted) goes through these instead of `feedback`/`updateFeedbackState`/`deleteFeedbackState`/
  // `isDupTitle` directly, so the same JSX-construction code works unchanged whether branching is
  // on or off: it reads/writes whichever branch is active in the scroller, or the block's
  // top-level feedback when branching is off (which also doubles as evaluateDropdown's fallback
  // when branching is on but nothing resolved — see resolveEffectiveOptions in @agavi/blocks).
  // Gated on gradingMode itself (not just conditionalBranches existing) — branches are authored
  // exclusively in graded mode, and switching away must make every downstream reader (feedback
  // routing just below, effectiveOptionsForActive, the options-list editor's role display) fall
  // back to the plain static/top-level shape, matching what Participation/Optional actually are:
  // no-correctness modes with nothing left to resolve. Branch data itself is untouched by this —
  // it's simply not read while not graded, so cycling back to graded needs no restore step at all.
  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;
  /**
   * The scenario row on screen, chosen by THE LEADER'S OWN TEST POINTER.
   *
   * Not a pointer of this block's. At runtime a whole group resolves from one key —
   * `@version:<leaderId>`, which is what lets a single `SessionRecord.rolls` entry explain every
   * answer in the cluster (§3.1) — and the editor has no business using a different rule. Read
   * through `useVersionInForce` on the LEADER, which is the very pointer that leader's own card
   * is already paging, so three things that must agree become one that cannot disagree: the
   * leader's prose on the canvas, this block's live test, and the row being authored.
   *
   * It costs one surprise, knowingly: rerolling the leader's card moves this editor to another
   * row mid-authoring. That is the group moving, which is honest — and the alternative is three
   * independent pointers drifting apart, which is the failure this whole feature exists to
   * prevent.
   *
   * Keyed on this block when it follows nothing, where it is simply this block's own test
   * pointer and goes unread. A hook cannot be called conditionally, and a dead key would be a
   * second thing to keep in step.
   */
  /**
   * The leader's variants, read LIVE from the leader block rather than copied here.
   *
   * §4.1's projection rule: a follower's rows ARE the leader's variants. Naming them from a
   * stored copy would be the same fact in two places, and the copy would be the one that goes
   * stale the first time a scenario is renamed.
   */
  /** The leader itself, not just its variants — the ghosted stem renders by its TYPE. */
  const leaderBlock = (blocks ?? []).find(b => b.id === storedData.followsBlockId);
  const leaderVersionsForPointer = ((blocks ?? []).find(b => b.id === storedData.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedData.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersionsForPointer?.[0]?.id;
  /**
   * WHAT THE TEST BAR'S PAGER PAGES. This block's own bank, or — when it follows one — the
   * leader's scenarios, on the shared pointer above. A follower has no bank of its own, so
   * without this the bar had no pager at all and there was no way to test a second scenario.
   */
  const follows = storedData.followsBlockId;
  const testPager = follows
    ? { versions: (leaderVersionsForPointer ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;
  /**
   * Gated on gradingMode for exactly the reason `activeBranch` is: an answer key is a graded-mode
   * fact, and a block switched to Participation has nothing for these rows to say.
   */
  const activeScenario = gradingMode === 'graded' && scenarioBranches && scenarioBranches.length > 0
    ? (scenarioBranches.find(b => b.scenarioId === shownScenarioId) ?? scenarioBranches[0])
    : undefined;
  /**
   * WHICHEVER answer setup the panel is editing, stripped to the two things every editing surface
   * below actually touches: the roles and the feedback.
   *
   * This is the whole reason a scenario follower needed almost no new UI. A numeric branch and a
   * scenario row differ only in what SELECTS them — a comparison against a variable, or a variant
   * of the leader — and the editor downstream of this line never asks that question. Writes still
   * dispatch (the two live in different arrays and key on different fields), but every read is
   * one path.
   */
  const activeSetup: { roles: Record<string, DropdownOption['role']>; feedback: DropdownFeedback } | undefined
    = activeScenario ?? activeBranch;
  /** @see leaderVersionsForPointer — the same list, named once the pointer above has used it. */
  const leaderVersions = leaderVersionsForPointer;
  /** The leader variant the active row answers, so the pager can name it. */
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) : null;
  /**
   * The leader's variant on the shared test pointer, for the TEST surface's ghosted copy of
   * the stem — deliberately NOT `activeScenarioVersion`.
   *
   * That one is gated on graded mode, correctly: it names the row whose ANSWER KEY is being
   * authored, and a participation block has none. The prose is a different question. A
   * participation block still asks about the scenario, and a teacher testing one still needs
   * to read it, so this reads the pointer and nothing else.
   */
  const shownScenarioVersion = storedData.followsBlockId
    ? scenarioInForce(leaderVersions, shownScenarioId) : null;
  /**
   * ONE pager, rendered in two places — the answer-setup box and the feedback panel.
   *
   * `VersionSelector` in browse mode, not a second chevron pair: its own doc is explicit that
   * two pagers a few inches apart must never disagree about which variant is on screen, and
   * browse mode (no `onAdd`, no `onRemove`) is also the projection rule (§4.1) in the UI — a
   * follower's rows are the leader's to add and remove.
   *
   * The noun stays off (the default) because both placements print the variant's name beside it.
   */
  /** The projection, kept live — a variant added to the leader becomes a row here. */
  useScenarioRowProjection<ScenarioRoleBranch<DropdownFeedback>>({
    followsBlockId: storedData.followsBlockId,
    blocks,
    rows: scenarioBranches,
    template: () => ({
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback,
    }),
    onSync: pushScenarioRows,
  });

  /*
   * THE OTHER PROJECTION: one version per row of an earlier block's answer space.
   *
   * The sibling of the call above and deliberately shorter. A follower's rows are the leader's
   * variants; a driven block's versions are the driver's rows. This one needs nothing about
   * dropdowns because `addVersion` already knows the answer key — see the hook's own note.
   */
  useDrivenVersionProjection({ block, data: storedData, emit, addVersion });

  /** ONE link control, rendered by whichever heading currently owns it — see the SCENARIO
   *  heading's note for why it moves. */
  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={storedData.versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  const scenarioLinkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedData.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyOptionsVersion}
    />
  );
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={leaderVersions ?? []}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (activeScenario) {
      // Writing a row's feedback IS reviewing it — the flag means "copied, not decided", and a
      // teacher who has just written this scenario's response has decided.
      pushScenarioRows(markScenarioReviewed(
        (scenarioBranches ?? []).map(b =>
          b.scenarioId === activeScenario.scenarioId ? { ...b, feedback: { states: nextStates } } : b),
        activeScenario.scenarioId,
      ));
    } else if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push(options, randomizeOrder, gradingMode, placeholder, feedback, nextBranches, conditionalEquation);
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push(options, randomizeOrder, gradingMode, placeholder, next, conditionalBranches, conditionalEquation);
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  // Role-per-option for whichever branch is active — same construction the option-list editor
  // already builds inline for its own display; centralized here so title-collision/staleness
  // checks against the active branch's own feedback can share it too.
  const effectiveOptionsForActive: DropdownOption[] = rolesForMode(gradingMode, activeSetup
    ? options.map(o => ({ ...o, role: activeSetup.roles[o.id] ?? ('incorrect' as const) }))
    : options);

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const [armedOptionId, setArmedOptionId] = useState<string | null>(null);
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Live test state for the collapsed canvas card — only meaningful while isSelected (this
  // block's own editor is open elsewhere). Resets whenever the editor closes, so testing
  // never leaves stale state behind for the next time this block is opened. The result itself
  // is NOT stored — it's re-derived from submittedSelectedId + the current (possibly
  // just-edited) block data on every render, so editing feedback text/content, adding or
  // removing a feedback state, or re-marking which option is correct all update the displayed
  // result immediately without needing to re-submit.
  //
  // testSelectedId is the widget's own live value (always up to date as the teacher interacts);
  // submittedSelectedId is what evaluation actually runs against — the two only diverge in
  // submit-gated mode (continuous === false), where changing the selection doesn't evaluate
  // until "Test Submit" is clicked. In continuous mode they're kept in lockstep on every change.
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);

  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  // Memoised for its IDENTITY, not its cost: `?? {}` minted a fresh object every render, so
  // anything depending on `rolledValues` re-ran on every render in Preview. Harmless where the
  // consumer writes to a ref; wasteful everywhere else.
  /* RECALLED ANSWERS RIDE THE SAME MAP — see `useRolledWithRecalls`, which the value-family
     blocks have done all along. Without it an option formula holding `[ara_…]` evaluated against
     a scope that had never heard of it and came out blank. */
  const rolledValues = useRolledWithRecalls(useMemo(
    () => (isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues),
    [isPreview, taskPreviewRoll, ownRolledValues],
  ));
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   */
  /* Tolerant of both shapes, exactly as MCQ is — see its own note. */
  const priorRaw = usePriorAnswer<string | null | { selectedId?: string | null }>();
  const prior = (priorRaw && typeof priorRaw === 'object') ? priorRaw.selectedId : priorRaw;
  const [testSelectedId, setTestSelectedId] = useState<string | null>(prior ?? null);
  const [submittedSelectedId, setSubmittedSelectedId] = useState<string | null>(prior ?? null);
  const [hasSubmitted, setHasSubmitted] = useState(priorRaw !== undefined);
  /* The SUBMITTED value, with the chosen option's resolved text beside its id — see MCQ. */
  const reportedAnswer = useMemo(() => {
    if (!hasSubmitted) return undefined;
    const opt = options.find(o => o.id === submittedSelectedId);
    return {
      selectedId: submittedSelectedId,
      selectedTexts: opt ? [processVariableChipHTML(opt.text, rolledValues)] : [],
    };
  }, [hasSubmitted, submittedSelectedId, options, rolledValues]);
  useReportAnswer(reportedAnswer);
  const [continuous, setContinuous] = useState(true);
  // Phone-only Edit/Test toggle inside the full-screen editor (see InputBlockFrame's own doc
  // comment) — owned here, not inside InputBlockFrame, so this component's own "jump to editing
  // this branch" action (the topNotices "Edit" button, built below) can flip straight back to
  // 'edit' mode itself, same instance, no cross-mount signal needed the way the desktop
  // collapsed-card/editor split requires.
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  // Student view shuffles option order (per shuffleWithAnchors) when randomizeOrder is on — the
  // live-test widget needs to reflect that too, or "randomize" and "pin" would look like they do
  // nothing while testing. Shuffled ONCE per test session (entering isSelected), not recomputed
  // every render, or the list would visibly reshuffle under the teacher's cursor on every
  // keystroke/submit. Stored as an id order (not the option objects themselves) so it survives
  // concurrent edits to option text/roles made from the feedback panel while still testing.
  //
  // Reads block.data fresh (via initData), NOT the local options/randomizeOrder state — the
  // canvas card and the open editor are two separate mounted instances of this component (one
  // rendered collapsed/isSelected, one rendered via InputBlockFrame), each with its own local
  // state seeded once at mount. Only props (block.data) are guaranteed to reflect the other
  // instance's edits; local state here would silently stay stuck at whatever it was when this
  // particular instance first mounted.
  const [testOptionOrder, setTestOptionOrder] = useState<string[] | null>(null);
  useEffect(() => {
    // Also seeded for `isPreview` — the student-facing Preview render needs a real rolled value
    // and a stable shuffle snapshot exactly like the isSelected test widget does, rather than
    // showing raw unresolved variable chips or reshuffling on every incidental re-render.
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data, placeholderDefault);
      setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      } else if (!isPreview) {
      setTestSelectedId(null);
      setSubmittedSelectedId(null);
      setHasSubmitted(false);
      setContinuous(true);
      setTestOptionOrder(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);
  // Phone's own entry/exit into the Test tab — mirrors the isSelected effect above exactly
  // (isSelected is always false on phone, since block selection there is tracked via
  // mobileEditingId, not selectedBlockId, so that effect alone never fires here). Without this,
  // rolledValues stayed at its initial `{}` until the teacher manually hit Reroll — variables
  // would show unresolved the first time a teacher opened the Test tab.
  useEffect(() => {
    if (phoneTestMode === 'test') {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data, placeholderDefault);
      setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
    // NOT in Preview, for the same reason as the `isSelected` effect above: this clears the
    // test widget when the PHONE editor leaves its Test tab, and on a desktop Preview
    // `phoneTestMode` is 'edit' forever, so it ran on mount and wiped the restored answer.
    } else if (!isPreview) {
      setTestSelectedId(null);
      setSubmittedSelectedId(null);
      setHasSubmitted(false);
      setContinuous(true);
      setTestOptionOrder(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);
  // Preview's single bottom Submit (see task-content.tsx's own doc comment on the signal) —
  // reacts to the signal CHANGING, not a per-block Submit click, since Preview has no per-block
  // Submit button any more. Reuses the exact same submittedSelectedId/hasSubmitted state (and
  // therefore the exact same feedback-firing/resolvedD machinery) the live test widget already
  // uses — Preview is just a different trigger for the same evaluation, not a separate one.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedSelectedId(testSelectedId);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  function handleTestChange(id: string | null) {
    setTestSelectedId(id);
    if (continuous) { setSubmittedSelectedId(id); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedSelectedId(testSelectedId);
    setHasSubmitted(true);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedSelectedId(testSelectedId); setHasSubmitted(true); }
  }
  // Re-reads live `options` (including current anchored/pinned flags) each time it's called —
  // so a pin toggled in the option-list editor while testing takes effect the next time the
  // order is re-rolled (reset or reroll), without needing to reshuffle continuously on every
  // keystroke (which would visibly jump the list under the teacher's cursor while they type).
  function rerollTestOptionOrder() {
    const current = initData(block.data, placeholderDefault);
    setTestOptionOrder(current.randomizeOrder ? shuffleWithAnchors(current.options).map(o => o.id) : null);
  }
  function handleTestReset() {
    setTestSelectedId(null);
    setSubmittedSelectedId(null);
    setHasSubmitted(false);
    /* NOT re-randomised. The testing block is a TEACHER'S INSTRUMENT: Reset means "same question,
       let me try a different answer", so it must change nothing about the question — including the
       option order. Re-shuffling here made a test unrepeatable, because a different outcome could
       come from the new answer or from the new layout and nothing said which. "A fresh student
       gets a fresh order" is true, and it belongs to Preview, where a reset really is a new
       attempt. Reroll is how a teacher asks for a new order on purpose. */
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
    rerollTestOptionOrder();
  }
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const questionEditorRef = useRef<Editor | null>(null);
  const optionEditorsRef = useRef<Map<string, Editor>>(new Map());
  const gradedSnapshotRef = useRef<DropdownOption[] | null>(null);
  // Keyboard-only focus lands on <body> once a row's own remove button unmounts — these give
  // every "confirm delete" handler a stable, always-present fallback to refocus, so a
  // keyboard/screen-reader user's place in the editor isn't lost on every delete.
  const addOptionButtonRef = useRef<HTMLButtonElement | null>(null);
  // Deleting a branch keeps this whole nav row mounted (it just recomputes which branch is
  // "active"), unlike an option row, but the "Previous condition" button is a stable target
  // regardless — used defensively so focus never depends on exactly how React reconciles this.
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);

  /** Applies a resync/filter transform to EVERY independent feedback set a dropdown has — the
   * top-level one plus each branch's own — since an option's text, position, or removal affects
   * every targeted feedback that references it, not just whichever branch happens to be active in
   * the scroller right now. */
  function updateAllFeedbackSets(
    transform: (states: FeedbackState[]) => FeedbackState[],
  ): { feedback: DropdownFeedback; branches: ConditionalRoleBranch[] | undefined } {
    return {
      feedback: { states: transform(feedback.states) },
      branches: conditionalBranches?.map(b => ({ ...b, feedback: { states: transform(b.feedback.states) } })),
    };
  }

  function push(opts = options, rand = randomizeOrder, mode = gradingMode, ph = placeholder, fdbk = feedback, cb = conditionalBranches, eq = conditionalEquation, asEq = optionsAsEquations, angMode = angleMode) {
    // The retained draft rides along on every ordinary save, so editing anything else cannot
    // drop it. Deliberately not a parameter: a defaulted parameter cannot express "clear this".
    emitFields({ question: questionHtmlRef.current, options: opts, randomizeOrder: rand, gradingMode: mode, placeholder: ph, feedback: fdbk, conditionalEquation: eq, conditionalBranches: cb, conditionalBranchesDraft: savedConditionalDraftRef.current, optionsAsEquations: asEq, angleMode: angMode });
  }

  /**
   * Persists branching as CLEARED. Needed because `push` cannot express it: a JS default parameter
   * fires on `undefined`, so `push(..., undefined, undefined)` does not clear the branches, it
   * restores `cb = conditionalBranches` — the very values being removed. Toggling branching off
   * therefore looked like it worked (the UI reads local state, already set to undefined) while the
   * saved block kept its branches: the canvas card, which grades off the saved block, went on
   * resolving a branch the editor said was off, and reopening the block showed the toggle back on.
   *
   * The authored set is still recoverable for as long as this editor stays open — the draft is held
   * in `savedConditionalDraftRef` and re-ticking the box restores it. Closing drops the draft, and
   * from there recovery is undo.
   */
  /**
   * Every write that changes the branch state itself, stated explicitly. It cannot go through `push`:
   * a JS default parameter fires on `undefined`, so `push(..., undefined, undefined)` restores
   * `cb = conditionalBranches` rather than clearing it — the trap that made "turn branching off"
   * silently never stick, leaving the canvas card grading against branches the editor said were off.
   */
  function pushBranchState(
    branches: ConditionalRoleBranch[] | undefined,
    equation: string | undefined,
    draft: DropdownBlockData['conditionalBranchesDraft'],
  ) {
    emitFields({ question: questionHtmlRef.current, options, randomizeOrder, gradingMode, placeholder, feedback, conditionalEquation: equation, conditionalBranches: branches, conditionalBranchesDraft: draft, optionsAsEquations, angleMode });
  }

  /**
   * Every write to the scenario rows, state and storage together.
   *
   * Separate from `push` for the same reason `pushBranchState` is: `push`'s parameters are
   * defaulted, and a defaulted parameter cannot say "clear this". It is also the only writer, so
   * local state and stored data cannot drift — the trap that would show a teacher rows the saved
   * block does not have.
   */
  function pushScenarioRows(rows: ScenarioRoleBranch<DropdownFeedback>[] | undefined) {
    setScenarioBranches(rows);
    emitFields({ scenarioBranches: rows });
  }

  function handleGradingMode(mode: DropdownBlockData['gradingMode']) {
    let nextOptions = options;
    if (gradingMode === 'graded' && mode !== 'graded') {
      // Leaving graded — remember the correct/incorrect setup and lock everything to neutral.
      gradedSnapshotRef.current = options;
      nextOptions = options.map(o => ({ ...o, role: 'neutral' as const }));
      setOptions(nextOptions);
    } else if (gradingMode !== 'graded' && mode === 'graded') {
      // Returning to graded — restore the prior setup instead of losing it.
      const snapshot = gradedSnapshotRef.current;
      const roleById = snapshot ? new Map(snapshot.map(o => [o.id, o.role])) : null;
      nextOptions = options.map((o, i) => ({
        ...o,
        role: (roleById ? (roleById.get(o.id) ?? 'incorrect') : (i === 0 ? 'correct' : 'incorrect')) as DropdownOption['role'],
      }));
      setOptions(nextOptions);
    }
    // Branches themselves are untouched here — `activeBranch` (below) is gated on gradingMode
    // being 'graded', so a branch's own roles are simply never read/displayed outside graded mode
    // at all, and nothing needs snapshotting or restoring to make that true.

    // Retrigger the positive slot (correct/any_answer/any_response) to match the new mode — see
    // retriggerPositiveSlot's doc comment for why this is a rename, not just a relabel.
    const nextFeedback = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedback !== feedback.states) setFeedback({ states: nextFeedback });

    setGradingMode(mode);
    push(nextOptions, randomizeOrder, mode, placeholder, { states: nextFeedback });
  }

  function toggleAnchor(id: string) {
    const next = options.map(o => o.id === id ? { ...o, anchored: !o.anchored } : o);
    setOptions(next);
    push(next);
  }

  function updateOptionText(id: string, text: string) {
    const next = options.map(o => o.id === id ? { ...o, text } : o);
    setOptions(next);
    // Keep any feedback targeting this option's title in sync with its new text — otherwise
    // renaming "Red" to "Crimson" would leave a stale "Selected Red" title behind. Every branch's
    // own feedback needs this too, not just whichever one is active in the scroller.
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncDropdownTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, randomizeOrder, gradingMode, placeholder, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Mirrors updateOptionText exactly, for the `formula` field instead of `text` — feedback
   * titles are resynced the same way (dropdownOptionLabel reads `formula` once optionsAsEquations
   * is on, so a title needs the same resync on a formula edit that it already gets on a text
   * edit). `text` is deliberately left untouched here — see the toggle contract on
   * DropdownBlockData.optionsAsEquations for why both fields persist independently. */
  function updateOptionFormula(id: string, formula: string) {
    const next = options.map(o => o.id === id ? { ...o, formula } : o);
    setOptions(next);
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncDropdownTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, randomizeOrder, gradingMode, placeholder, nextFeedback, nextBranches ?? conditionalBranches);
  }

  function handleRoleClick(id: string) {
    if (gradingMode !== 'graded') return;
    // Radio behavior: clicking a non-correct option makes it correct
    if (options.find(o => o.id === id)?.role === 'correct') return;
    const next = options.map(o => ({ ...o, role: (o.id === id ? 'correct' : 'incorrect') as DropdownOption['role'] }));
    setOptions(next);
    // Every targeted feedback's evaluation mirrors its target option's role — keep them in
    // sync whenever a role changes, rather than letting the two silently drift apart.
    const nextFeedback = { states: resyncDropdownTargetedEvaluations(next, feedback.states) };
    setFeedback(nextFeedback);
    push(next, randomizeOrder, gradingMode, placeholder, nextFeedback);
  }

  function addOption() {
    const next = [...options, makeOption(gradingMode === 'graded' ? 'incorrect' : 'neutral')];
    setOptions(next);
    push(next);
  }

  function removeOption(id: string) {
    if (options.length <= 1) return;
    optionEditorsRef.current.delete(id);
    const removedIsCorrect = options.find(o => o.id === id)?.role === 'correct';
    const next = options.filter(o => o.id !== id);
    if (removedIsCorrect && next.length > 0) next[0] = { ...next[0]!, role: 'correct' };
    // Deleting an option also deletes any feedback tied to it — recoverable only through undo,
    // not silently left behind as an orphaned, mislabeled card. Remaining feedback titles are
    // resynced too, since a blank option's fallback label ("Option 2") is position-based and
    // every option after the removed one just shifted down by one. Applied across every branch's
    // own feedback, not just whichever is active — an option disappearing affects all of them.
    const { feedback: nextFeedback, branches: nextBranchesFromFeedback } = updateAllFeedbackSets(
      states => resyncDropdownTargetedTitles(next, states.filter(s => !(s.type === 'targeted' && s.trigger === `option_${id}`)), t, optionsAsEquations)
    );
    // A branch's OWN `roles` map isn't touched by the reassignment above (that's only the static
    // `options` array) — left alone, a branch whose correct pick was just deleted would silently
    // end up with no correct option at all (a real "none-correct" setup bug the teacher never
    // asked for), unlike the static case, which auto-heals. Mirrors that same behavior per branch:
    // promote the same first remaining option, and drop the now-meaningless stale entry for the
    // removed option id.
    const nextBranches = nextBranchesFromFeedback?.map(b => {
      const roles: Record<string, DropdownOption['role']> = Object.fromEntries(Object.entries(b.roles).filter(([optId]) => optId !== id));
      if (b.roles[id] === 'correct' && next.length > 0) roles[next[0]!.id] = 'correct';
      return { ...b, roles };
    });
    setOptions(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, randomizeOrder, gradingMode, placeholder, nextFeedback, nextBranches ?? conditionalBranches);
    // The just-removed row's own remove button (whatever had focus) unmounts with it, dropping
    // keyboard focus to <body> — refocus the one control guaranteed to still exist afterward.
    addOptionButtonRef.current?.focus();
  }

  function handleOptionDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = options.findIndex(o => o.id === active.id);
    const to = options.findIndex(o => o.id === over.id);
    if (from === -1 || to === -1) return;
    const next = arrayMove(options, from, to);
    setOptions(next);
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncDropdownTargetedTitles(next, states, t, optionsAsEquations)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, randomizeOrder, gradingMode, placeholder, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Full copy, fresh ids, no content changes — used only for the very first branch, which isn't
   * a fork of anything: it's the same feedback that already existed, simply moving to live under
   * a branch instead of at the top level (which stays as-is going forward, now serving as
   * evaluateDropdown's "no branch matched" fallback — see resolveEffectiveOptions in
   * @agavi/blocks). Fresh ids so updating branch 1's Correct card can never ambiguously also
   * match the top-level Correct card sharing the same id. */
  function cloneFeedbackWithFreshIds(source: DropdownFeedback): DropdownFeedback {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Structure carries over (which options have a targeted entry, and their order), but each
   * state's own content resets to blank if it's been customized, or carries forward unchanged if
   * it still matches that slot's default — see resetContentIfCustomized's doc comment for why
   * blank, not default text. Used for every branch after the first, seeded from whichever branch
   * was active when "add" was clicked (always the last one, since that's the only branch the add
   * button is ever reachable from). */
  function seedBranchFeedback(source: DropdownFeedback): DropdownFeedback {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching only exists in graded mode (showConditionalUI requires it), so the positive
          // slot inside a branch is always the 'correct' trigger, never any_answer/any_response.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  function makeConditionalBranch(seedFrom: DropdownFeedback, mode: 'migrate' | 'fork'): ConditionalRoleBranch {
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback: mode === 'migrate' ? cloneFeedbackWithFreshIds(seedFrom) : seedBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      // Restore the draft from the last time this session turned the toggle off, if any —
      // otherwise start fresh, same as before.
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      push(options, randomizeOrder, gradingMode, placeholder, feedback, branches, equation);
    } else {
      // Banked under its own key and out of `conditionalBranches` at once. Recovery from an
      // accidental untick needs the set to survive, but the canvas card grades off the saved block,
      // so leaving it in place would have the card resolving a branch the editor says is off. A
      // separate key gives both, and unlike a memory-only draft it survives a reload.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      pushBranchState(undefined, undefined, draft);
    }
  }

  // A variable's presence in the question/options is what makes branch-based correctness
  // meaningful at all — showConditionalUI (below) hides the whole toggle+equation+branch UI once
  // the last variable chip is removed, but hiding it isn't enough on its own: the branches were
  // still sitting there, still consulted by evaluateDropdown, just no longer visible or editable
  // by the teacher — a real leakage bug (a student's answer keeps grading against conditions
  // nobody can see anymore). Runs every render (no dependency array) rather than trying to track
  // `questionHtmlRef.current` — it's a ref, not state, so there's no single dependency that would
  // reliably fire only on the renders where it actually changed; the check itself is cheap enough
  // that re-running it every render costs nothing, and clearing branches makes the condition false
  // on the next check, so there's no risk of looping.
  useEffect(() => {
    // The collapsed canvas card is a separate mounted instance whose local `options`/
    // `conditionalBranches` are frozen at whatever they were on its own mount (see the
    // isSelected/isEditing doc comment above) — this only means anything on the live editor.
    if (!isEditing) return;
    // Question-only — standing rule (2026-08-02, applied uniformly across MCQ/Dropdown/Ordering):
    // "Correctness depends on variables" is a fact about the QUESTION's premise changing;
    // branching's role map is always a hand-authored declaration keyed on the shared equation's
    // value, never derived from evaluating an option, so a variable merely appearing in an option
    // carries no signal that the scenario has conditions under which correctness changes. This
    // toggle and "Treat options as equations" (gated by `hasVariableInOptions` below) are a
    // one-to-one correlation with where the variable lives, not a shared OR gate.
    const hasVariableNow = htmlHasVariableChips(questionHtmlRef.current);
    if (!hasVariableNow && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push(options, randomizeOrder, gradingMode, placeholder, feedback, conditionalBranches, val);
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push(options, randomizeOrder, gradingMode, placeholder, feedback, next);
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push(options, randomizeOrder, gradingMode, placeholder, feedback, next);
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalRoleBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push(options, randomizeOrder, gradingMode, placeholder, feedback, next);
  }

  function handleBranchRoleClick(branchId: string, optionId: string) {
    const next = (conditionalBranches ?? []).map(b => {
      if (b.id !== branchId || b.roles[optionId] === 'correct') return b;
      const nextRoles: Record<string, DropdownOption['role']> = Object.fromEntries(
        options.map(o => [o.id, (o.id === optionId ? 'correct' : 'incorrect') as DropdownOption['role']])
      );
      // Every targeted feedback's evaluation mirrors its target option's role WITHIN this
      // branch — same idea as handleRoleClick's resync for the static (no-branch) case, just
      // scoped to this branch's own targeted feedback and its own role snapshot.
      const effectiveOptionsForBranch = options.map(o => ({ ...o, role: nextRoles[o.id] ?? ('incorrect' as const) }));
      return { ...b, roles: nextRoles, feedback: { states: resyncDropdownTargetedEvaluations(effectiveOptionsForBranch, b.feedback.states) } };
    });
    setConditionalBranches(next);
    push(options, randomizeOrder, gradingMode, placeholder, feedback, next);
  }

  /**
   * Set this scenario's correct option — `handleBranchRoleClick` keyed by variant instead of by
   * branch id, plus the review flag.
   *
   * Not folded into that function despite the near-identical body: the two write different arrays
   * through different persisters, so a shared one would be a conditional at every line that
   * matters and would read as one thing doing two. The targeted-feedback resync is the part worth
   * keeping identical, and it is — same call, same arguments.
   */
  function handleScenarioRoleClick(scenarioId: string, optionId: string) {
    const next = (scenarioBranches ?? []).map(b => {
      if (b.scenarioId !== scenarioId || b.roles[optionId] === 'correct') return b;
      const nextRoles: Record<string, DropdownOption['role']> = Object.fromEntries(
        options.map(o => [o.id, (o.id === optionId ? 'correct' : 'incorrect') as DropdownOption['role']])
      );
      const effectiveOptionsForRow = options.map(o => ({ ...o, role: nextRoles[o.id] ?? ('incorrect' as const) }));
      return { ...b, roles: nextRoles, feedback: { states: resyncDropdownTargetedEvaluations(effectiveOptionsForRow, b.feedback.states) } };
    });
    pushScenarioRows(markScenarioReviewed(next, scenarioId));
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const usedTriggers = new Set(activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger));

    /**
     * A blank is one of the answers an OPTIONAL block can receive, so it belongs in the same list
     * as the answers — which is also what lets the catch-all close once the list is complete.
     * Appended like any other card; the list order is the teacher's.
     */

    // Outside graded no option is correct, so every one of them can carry a targeted state.
    const firstAvailableIdx = effectiveOptionsForActive.findIndex(o =>
      (gradingMode !== 'graded' || o.role !== 'correct') && !usedTriggers.has(`option_${o.id}`));
    const firstAvailable = firstAvailableIdx === -1 ? undefined : effectiveOptionsForActive[firstAvailableIdx];
    if (!firstAvailable) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: dropdownTargetedTitle(firstAvailable, t, optionsAsEquations),
      trigger: `option_${firstAvailable.id}`,
      content: '', evaluation: firstAvailable.role === 'neutral' ? 'neutral' : 'incorrect',
    };
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }

  function handleDone() {
    // Non-destructive toggling between richtext and formula only holds for as long as this
    // editing session stays open — see DropdownBlockData.optionsAsEquations' doc comment and
    // DropdownOption.formula's. On close, whichever field is INACTIVE gets pruned rather than
    // carried forward indefinitely: otherwise a teacher who sets up formulas, closes, comes back
    // later, and unchecks the toggle would be surprised by long-stale richtext resurfacing (or
    // vice versa). Undo is the recovery path if this was flipped by accident, same as any other
    // edit made in this session.
    const prunedOptions = options.map(o => optionsAsEquations ? { ...o, text: '' } : { ...o, formula: undefined });
    if (prunedOptions.some((o, i) => o.text !== options[i]!.text || o.formula !== options[i]!.formula)) {
      setOptions(prunedOptions);
    }
    push(prunedOptions);
    // Closing is what makes a switched-off branch set unrecoverable: the retained draft is
    // dropped from the saved block and undo becomes the only way back.
    if (savedConditionalDraftRef.current !== undefined && conditionalBranches === undefined) pushBranchState(undefined, undefined, undefined);
    savedConditionalDraftRef.current = undefined;
    // Same moment, same reason: closing is what makes an unlink unrecoverable.
    linkSwapRef.current = {};
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;
  const gradingModeChipClass = gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';

  // ── Preview ─────────────────────────────────────────────────────────────────

  if (isPreview) {
    // Same resolution pipeline the live-test widget uses (computeDropdownTestData's own
    // resolvedD) — a variable chip decoratively substituted, or an option formula actually
    // evaluated to a number when optionsAsEquations is on, exactly as a real student would see it.
    // Reading raw `d.options`/`d.question` here instead (as this used to) meant an equation-mode
    // dropdown previewed as entirely blank options (text is pruned to '' in that mode — see
    // handleDone) and any variable chip rendered as bare unstyled markup with no BlockPreviewProvider
    // context to resolve it. rolledValues/testOptionOrder are seeded by the same effect the
    // isSelected branch below uses, gated on `isPreview` too (see its own comment).
    //
    // Genuinely interactive now, not a disabled snapshot — Preview behaves exactly like the live
    // test widget (pick an option, see real feedback once submitted), just triggered by the
    // page-level bottom Submit (see the previewSubmitSignal effect above) instead of a per-block
    // one, so answers across every block on the page evaluate together in one action.
    const rawD = initData(block.data, placeholderDefault);
    const d = buildNormalizedDropdownData(rawD, t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const { resolvedD, testResult } = memoizedByKey(
      /* The DRAWN VERSION is part of the key, or this cache answers for whichever version it saw
         first and the content never moves — see mcq-block.tsx's note. */
      dropdownTestDataCache, block.data,
      [variables, t, rolledValues, submittedSelectedId, hasSubmitted, i18n.language,
       taskPreviewRoll?.[versionRollKey(block.id)],
       /* The LEADER's draw is part of the key too, for the same reason this block's own is: the
          scenario decides the answer key, so a cache that ignored it would grade one scenario's
          answer against another's roles. */
       drawnScenarioId(d.followsBlockId, taskPreviewRoll)],
      () => computeDropdownTestData(d, variables, t, rolledValues, submittedSelectedId, hasSubmitted, i18n.language,
        // The ONE task roll both blocks read, which is what makes the leader's prose and this
        // block's answer key describe the same scenario (§3.1).
        drawnScenarioId(d.followsBlockId, taskPreviewRoll)),
    );
    /* The same derivation asked about the DRAFT: `hasSubmitted` forced true so the chips light
       as though this answer had been handed in. Reported, never rendered -- Preview still shows
       a student nothing until they submit. */
    const { testChips: draftChips, testResult: draftStates } = memoizedByKey(
      dropdownDraftChipsCache, block.data,
      [variables, t, rolledValues, testSelectedId, i18n.language,
       taskPreviewRoll?.[versionRollKey(block.id)], drawnScenarioId(d.followsBlockId, taskPreviewRoll)],
      () => computeDropdownTestData(d, variables, t, rolledValues, testSelectedId, true, i18n.language, drawnScenarioId(d.followsBlockId, taskPreviewRoll)),
    );
    return (
      <DropdownAnswerDisplay
        question={resolvedD.question}
        options={resolvedD.options}
        optionOrder={testOptionOrder}
        rolledValues={rolledValues}
        value={testSelectedId}
        onChange={setTestSelectedId}
        placeholder={d.placeholder}
      >
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftChips} states={draftStates} />
        {renderDropdownFiredFeedback(testResult, rolledValues)}
      </DropdownAnswerDisplay>
    );
  }

  // ── Collapsed ────────────────────────────────────────────────────────────────

  if (!isEditing) {
    const rawD = initData(block.data, placeholderDefault);
    // A block just created via "Add Dropdown block" persists `data: {}` — `block.data.feedback`
    // is genuinely absent, not merely a raw `{states: []}` some other path already defaulted.
    // The editor's own `feedback` state (via useInputBlockFeedback) always runs this same
    // normalization on mount, so it never shows this gap — but this collapsed/isSelected instance
    // reads `block.data` directly and would otherwise compute empty `legendStates`/`testChips`
    // for a brand-new block, which the shared TestBar then (wrongly) reports as "No feedback
    // options available in Optional mode" even in Has Correct/graded mode. Backfilled here with
    // the exact same defaults the editor would have produced, so a never-yet-opened fresh block's
    // live-test legend shows its real No Answer/Correct/Incorrect states immediately.
    const d = buildNormalizedDropdownData(rawD, t, shownVersionId);
    // Variable-driven correctness (see ConditionalRoleBranch in @agavi/blocks), the equation's
    // resolved value, the fired feedback result, the chip legend, etc. — see
    // computeDropdownTestData's own doc comment for why this is a shared function rather than
    // inline here: the phone full-screen editor's own "Test" tab needs the exact same derivation,
    // just built from the editor's live local state instead of `initData(block.data)`.
    const {
      matchedBranch, matchedBranchIndex, matchedEquationValue,
      testResult, resolvedD, testChips, hasChips, hasRollable,
    } = memoizedByKey(
      // Keyed on the shown version too — see the preview branch's note.
      dropdownTestDataCache, block.data,
      [variables, t, rolledValues, submittedSelectedId, hasSubmitted, i18n.language, shownVersionId, shownScenarioId],
      /*
       * On the CARD, the scenario is whichever one the pager is on — not a draw.
       *
       * A draw here would be a different scenario from the one the editor is set up for, every
       * time the card re-rendered, which makes the live test unusable for the thing it is for:
       * checking the setup you are looking at. Preview is where a real draw belongs, and that is
       * where it happens.
       */
      () => computeDropdownTestData(d, variables, t, rolledValues, submittedSelectedId, hasSubmitted, i18n.language, activeScenario?.scenarioId),
    );
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dropdownAriaLabel = d.question
      ? t('blocks.dropdown.editAriaBaseWithQuestion', { question: stripHtmlForLabel(d.question).slice(0, 80) })
      : t('blocks.dropdown.editAriaBase');

    // Every currently-active issue for this block, computed once — read by this bar (top-priority
    // one only), the test-panel diagnostics list (all of them), and the in-editor banner (all of
    // them too), so the three surfaces can never disagree about wording, and nothing is ever
    // computed in only one of them (see dropdownIssues' own doc comment).
    /* What a recall chip in these options could show. Rebuilt per render and therefore keyed
       into the cache below by CONTENT — its identity would recompute every block's issues on
       every render, and leaving it out would hold a stale warning after the source's options
       were edited in the other task. */
    const recalls = recallOptionSpaces(d.options.map(o => o.text), scope, variables);
    const issues = settleStored(memoizedByKey(dropdownIssuesCache, block.data, [recallSpacesKey(recalls), variables, t, shownVersionId], () => dropdownIssues(d, { ...issueCtx, recalls })));
    const issueBar = collapsedIssueBar(issues);
    const barNode = (() => {
      if (issueBar) return issueBar;
      if (isMain) return null;
      return collapsedModeBar(d.gradingMode, t, 'dropdown');
    })();

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<SquareChevronDown className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.dropdown.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={gradingModeChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={dropdownAriaLabel}
        answerBarSlot={() => barNode}
        testEvaluation={testResult ? (testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testChips,
          onReset: handleTestReset,
          // Versions are rollable too: a banked block can be rerolled with no chips in it.
          /* OFFERED WHENEVER ANYTHING IS RANDOM, which includes `randomizeOrder` on its own.
       Now that Reset keeps the order, a block that shuffles but has no variable and no bank would
       otherwise have no way to see a second arrangement at all. Same reasoning Sort's own gate
       records. */
    onReroll: ((hasChips && hasRollable) || randomizeOrder) ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump straight to editing it —
          // deliberately NOT automatic on every reroll (see pendingBranchJump's doc comment): only
          // fires when the teacher explicitly wants to fix what they just noticed. Teacher-only
          // diagnostic, so it belongs in this panel, not `children` (the frozen, student-facing
          // content area). Rendered as `topNotices` (right under the controls row, above the chip
          // legend) rather than `notices` — "which branch just resolved" is what happened, read
          // first; the issues list below is what's still wrong, read after the legend.
          /* One row for everything this test resolved to — the version it drew and, inside it,
             the branch that matched. Shared with the phone bar below and with MCQ: see
             `version-branch-notice.tsx` for the layout's reasoning and the rule that a scope tag
             is never truncated. */
          topNotices: (
            <VersionBranchNotice
              versions={testPager?.versions ?? shownVersions}
              activeVersionId={testPager ? testPager.activeId : shownVersionId}
              onSelectVersion={testPager?.onSelect ?? setTestVersion}
              noun={testPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={matchedBranch && matchedBranchIndex !== -1 ? {
                index: matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: matchedEquationValue,
                operator: matchedBranch.operator,
                threshold: matchedBranch.threshold,
                thresholdHigh: matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (matchedBranch && matchedBranchIndex !== -1) requestBranchJump(block.id, matchedBranchIndex);
                editShownVersion(matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected ? (
          <DropdownAnswerDisplay
            question={d.question}
            options={resolvedD.options}
            optionOrder={testOptionOrder}
            rolledValues={rolledValues}
            value={testSelectedId}
            onChange={handleTestChange}
            placeholder={d.placeholder}
            scenario={<ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />}
          >
            {renderDropdownFiredFeedback(testResult, rolledValues)}
          </DropdownAnswerDisplay>
        ) : (
          <>
            <BlockQuestion html={d.question} spaced showEmptyNote />
            {(() => {
              // Reads from d.options (raw), NOT resolvedD.options — this is the plain
              // collapsed canvas card, not a test/Preview surface, so a variable-driven option
              // should show its own chip/formula here, never an evaluated sample value (which
              // would otherwise stick around stale from whichever rolledValues happened to be
              // in memory from the last time this block was selected — see MCQ's own equivalent
              // fix in mcq-block.tsx's collapsed summary for the same reasoning).
              const correctOption = rolesForMode(d.gradingMode, d.options).find(o => o.role === 'correct');
              const correctContent = correctOption ? (d.optionsAsEquations ? (correctOption.formula ?? '') : correctOption.text) : '';
              return (
                <div className='mx-auto flex w-full items-start gap-2 rounded-md border border-ws-border-strong bg-ws-btn px-3 py-2 md:max-w-xs'>
                  {correctContent ? (
                    <>
                      <Check aria-hidden='true' className='mt-0.5 h-4 w-4 shrink-0 text-ws-success-fg' strokeWidth={3} />
                      {d.optionsAsEquations ? (
                        // A formula is a plain `{name}` text string, not an HTML chip node —
                        // PreviewHtml/renderChipsForDisplay only transforms `[data-variable-chip]`
                        // elements, so it leaves a formula's own `{name}` references as literal
                        // squiggly-brace text, unstyled. FormulaWithValues (already used for the
                        // branch-condition equation above) is the equivalent chip-render for a
                        // formula string — passing `{}` (not the live rolledValues) forces every
                        // reference to render as a bare styled chip, never an evaluated value,
                        // same "always chip on the display card" rule as the text branch.
                        <span className='min-w-0 flex-1 break-words text-sm text-ws-text'>
                          <FormulaWithValues formula={correctContent} rolledValues={{}} />
                        </span>
                      ) : (
                        <PreviewHtml html={correctContent} as='span' className='min-w-0 flex-1 break-words text-sm text-ws-text ws-formula-preview' />
                      )}
                    </>
                  ) : (
                    <span className='flex-1 text-sm text-ws-muted'>{d.placeholder}</span>
                  )}
                  <SquareChevronDown aria-hidden='true' className='mt-0.5 h-4 w-4 shrink-0 text-ws-muted' strokeWidth={2} />
                </div>
              );
            })()}
            <p className='mt-1.5 text-center text-xs text-ws-muted'>{t('blocks.dropdown.optionCount', { count: d.options.length })}</p>
          </>
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Feedback slots ───────────────────────────────────────────────────────────

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  const usedTriggers = new Set(targetedStates.map(s => s.trigger));
  // Outside graded every answer can carry a targeted state (nothing is correct, so nothing is
  // terminal), plus the blank in optional — see addTargetedState for why a blank is in this list.
  const canAddMore = effectiveOptionsForActive.some(o =>
    (gradingMode !== 'graded' || o.role !== 'correct') && !usedTriggers.has(`option_${o.id}`));

  // Feedback is stale (suppressed, not deleted) once its target option is marked
  // correct — hidden here rather than reassigned to a different option in the
  // trigger picker.
  const optionRoleById = new Map(effectiveOptionsForActive.map(o => [o.id, o.role]));
  const visibleTargetedStates = targetedStates.filter(s => {
    /**
     * A blank only fires in OPTIONAL. Graded and survey catch it as `unanswered` BEFORE anything
     * targeted runs, so the card could never fire there — it sat in the panel looking authored
     * and resolving to nothing.
     *
     * Hidden rather than deleted, like every other stale targeted state: switching back to
     * optional brings it and its text back, which is the same promise targeted feedback makes
     * across every other mode change.
     */
    if (!s.trigger.startsWith('option_')) return true;
    const id = s.trigger.slice('option_'.length);
    return optionRoleById.get(id) !== 'correct';
  });

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, visibleTargetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={visibleTargetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {visibleTargetedStates.map(s => {
            const usedByOthers = new Set(
              activeFeedbackStates.filter(st => st.type === 'targeted' && st.id !== s.id).map(st => st.trigger)
            );
            const triggerOptions: TriggerOption[] = effectiveOptionsForActive.flatMap((opt, i) => {
              if (opt.role === 'correct' || usedByOthers.has(`option_${opt.id}`)) return [];
              // bracketizeVariableChips, not stripHtmlForLabel — keeps rich formatting
              // (bold/italic/color) intact, since a teacher may rely on it to tell two
              // otherwise-identical-text options apart when picking a trigger; only the chip's
              // bare-name static serialization needs the `{name}` fix-up (see mcq-block.tsx's
              // identical fix for the same trigger-picker preview pattern).
              const rawPreview = (optionsAsEquations ? opt.formula : opt.text) || undefined;
              return [{
                value: `option_${opt.id}`,
                label: t('blocks.options.optionLabel', { number: i + 1 }),
                // Chips, not bracketized text — see labelChipHtml. A formula option showed its
                // literal `{name}` braces here and a rich-text one showed the chip's bare name.
                preview: rawPreview
                  ? labelChipHtml(rawPreview, { asFormula: !!optionsAsEquations, knownNames: new Set(variables.map(v => v.name)) })
                  : rawPreview,
                evaluation: (opt.role === 'incorrect' ? 'incorrect' : 'neutral') as TriggerOption['evaluation'],
              }];
            });
            const currentOpt = triggerOptions.find(o => o.value === s.trigger) ?? triggerOptions[0];
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                title={s.title} content={s.content} evaluation={displayEvaluation(gradingMode, s.evaluation)}
 /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
    had collapsed sprang back open every time they flipped modes to compare. */
 collapseSignal={gradingMode}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                titleLocked
                evalLocked
                triggerZone={<TriggerDropdown value={s.trigger} options={triggerOptions.map(o => ({ value: o.value, label: o.label, preview: o.preview ? authoredPreviewNode({ text: o.preview }) : undefined, evaluation: o.evaluation }))} onChange={trigger => {
                  const newId = trigger.slice('option_'.length);
                  const idx = effectiveOptionsForActive.findIndex(o => o.id === newId);
                  const opt = idx === -1 ? undefined : effectiveOptionsForActive[idx];
                  updateActiveFeedbackState(s.id, {
                    trigger,
                    title: opt ? dropdownTargetedTitle(opt, t, optionsAsEquations) : s.title,
                    evaluation: opt ? (opt.role === 'neutral' ? 'neutral' : 'incorrect') : s.evaluation,
                  });
                }} />}
                triggerZoneSummary={currentOpt ? <DropdownTriggerChip option={currentOpt} /> : undefined}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => {}}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // No positional "Option N" fallback — a blank option's Default Incorrect chip should read as
  // obviously unfinished (icon + empty pill), not as if "Option 2" were its real, authored text.
  // Shows the raw formula (not an evaluated number) when equations are on — same reasoning as
  // dropdownOptionLabel above: this is an authoring-facing summary, not the student-facing test
  // display, so "which formula" is more useful here than one arbitrary sample-resolved value.
  const optContent = (opt: DropdownOption) => dropdownOptionNode(opt, optionsAsEquations, chipScope);

  const coveredIds = new Set(
    activeFeedbackStates
      .filter(s => s.type === 'targeted' && s.trigger.startsWith('option_'))
      .map(s => s.trigger.slice('option_'.length))
      // Exclude stale feedback (target option since marked correct) — otherwise a
      // correct option with leftover feedback would wrongly appear "covered" and
      // drop out of the Default Correct list even though that feedback is inactive.
      .filter(id => optionRoleById.get(id) !== 'correct')
  );
  // Includes uncovered neutral options, not just incorrect ones — a neutral pick with
  // no targeted feedback still falls through to Default Incorrect at evaluation time.
  const panelReach = dropdownReachability({ gradingMode, feedback: { states: activeFeedbackStates } }, effectiveOptionsForActive);
  const panelIdx = (id: string) => effectiveOptionsForActive.findIndex(o => o.id === id);
  const uncoveredIncorrect = panelReach.uncoveredWrong.map((opt: DropdownOption) => ({ opt, idx: panelIdx(opt.id) }));
  const uncoveredCorrect  = effectiveOptionsForActive.map((o, i) => ({ opt: o, idx: i })).filter(({ opt }) => opt.role === 'correct'   && !coveredIds.has(opt.id));

  // The same two lists the test legend draws — see `dropdownChips`. Default Correct in particular:
  // the legend used to show ONE chip for the whole state while this fanned out per option.
  const chipRow = (chips: ConditionChips) => (
    <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );
  const defaultIncorrectInline = chipRow(dropdownChips('incorrect', panelReach, optContent));
  const defaultCorrectInline = chipRow(dropdownChips('correct', panelReach, optContent));

  // The generic (no-specific-option) chip for the positive slot in survey/optional mode — any
  // answer (survey) or any response including blank (optional) satisfies it, so unlike graded's
  // defaultCorrectInline there's no option list to show, just the fixed label for whichever mode
  // this is.
  /**
   * The catch-all LISTS what it still catches, exactly as Default Incorrect does in graded — a
   * generic pill left the teacher to work out which answers counted as "any", and that set
   * shrinks as targeted states claim them. Available here because this block's answer set is
   * CLOSED; the open-answer blocks say "any other X" instead and keep doing so.
   */
  const participationCatchAllInline = chipRow(catchAllChips(gradingMode, visibleTargetedStates.length > 0, t,
    () => dropdownChips('incorrect', panelReach, optContent, gradingMode)));
  const anyAnswerInline = participationCatchAllInline;
  const anyResponseInline = participationCatchAllInline;
  /**
   * An unreachable positive state shows the standard UnreachableChip in place of its trigger —
   * the treatment compass and numerical already use. Survey's Any Answer closes once every
   * option has a targeted state; optional's Any Response never does, a blank always reaching it.
   */
  const positiveUnreachable = gradingMode === 'graded'
    ? uncoveredCorrect.length === 0
    : !isReachable(panelReach, positiveTriggerForMode(gradingMode));
  const positiveTriggerZone = positiveUnreachable
    ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
    : gradingMode === 'survey' ? anyAnswerInline
    : gradingMode === 'optional' ? anyResponseInline
    : defaultCorrectInline;

  // A correct-marked option with no actual text is already flagged with wider reach elsewhere —
  // dropdownIssues' 'correct-option-blank'/'branch-*-correct-blank' entries (sticky bar +
  // test-panel notices, visible regardless of whether this Feedback section is even open) and
  // the amber/error border on the options-list container in the editor. A third, per-card copy
  // here would just restate the same fact next to a chip (defaultCorrectInline) that, since the
  // blank-stays-blank change above, already renders visibly empty for exactly this case — so it
  // was removed rather than kept as a fourth/fifth signal for one issue.

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={positiveUnreachable}
        triggerZone={positiveTriggerZone}
        triggerZoneSummary={positiveTriggerZone}
        title={catchAllTitle(gradingMode, visibleTargetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /**
         * The tick goes the moment a target exists — the catch-all must not affirm an answer the
         * teacher ignored more strongly than one they wrote for. With no targets the block is
         * still a plain questionnaire and keeps it. Same rule `catchAllChips` applies to the chip
         * beside it, so the card and its trigger cannot disagree.
         */
        evaluation={gradingMode !== 'graded' && visibleTargetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={uncoveredIncorrect.length === 0}
        triggerZone={defaultIncorrectInline}
        triggerZoneSummary={defaultIncorrectInline}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Live in-editor issues ────────────────────────────────────────────────────
  // The exact same computation the collapsed bar and test-panel diagnostics use, run against the
  // editor's own live state instead of `initData(block.data)` — this is what makes an issue like
  // "question is blank" visible the instant it's true, rather than only once the editor closes
  // and the collapsed view re-reads persisted data (the gap this whole scheme was built to close).
  // Also the same shape computeDropdownTestData needs for the phone Test tab below, so it's
  // named once and reused rather than rebuilt per call site.
  /* Enumerated, so a field left out is a check that silently never runs — `scenarioBranches`
     was, and the unreviewed count it feeds appeared nowhere. */
  const liveD: DropdownBlockData = { question: questionHtmlRef.current, options, randomizeOrder, placeholder, gradingMode, feedback, conditionalEquation, conditionalBranches, optionsAsEquations, angleMode, followsBlockId: storedData.followsBlockId, scenarioBranches };
  const editorIssues = settleLive(dropdownIssues(liveD, issueCtx));
  // ONE derivation, read from the issues themselves. This set used to re-run the duplicate and
  // collision checks a second time purely to decide which rows to ring, which is the pattern
  // `BlockIssue.fields` exists to end: the check that found the fault now says which rows it was
  // in, so the ring and the message cannot describe different rows.
  const collidingOptionIds = issueFieldIds(editorIssues);
  // Rendered as the frame's own sticky `warningBarSlot` (always visible at the bottom of the
  // editor, regardless of scroll) — a separate slot from `answerBarSlot` (which other migrated
  // blocks, e.g. numerical/MCQ, already use for their own current-correct-answer summary; this
  // says something different — "is this block's setup broken" — and needs its own dedicated
  // slot so it never collides with that in the future) — instead of a banner above the question
  // editor. missing-feedback-text is excluded because it already has its rightful, more specific
  // home: each blank feedback card shows its own warning directly (see feedback-card.tsx's
  // `missingContent`), so surfacing it a second time here would just be the same redundancy this
  // restructuring was meant to remove.
  // Only ERROR-severity issues get an input-level highlight — the idea is errors should annoy a
  // teacher into fixing them, while warnings (coverage overlap, missing feedback text) should
  // just be visible without adding visual noise on top of an indicator they already have
  // elsewhere (the overlap message itself; the slashed-bubble icon already on each blank
  // feedback card). `some(...)` instead of a plain key lookup so a caller can pass a whole
  // family of keys (e.g. every `branch-${id}-*` variant) without the caller needing to know the
  // exact branch id suffix.
  const hasEditorError = (...keys: string[]) => editorIssues.some(i => i.severity === 'error' && keys.includes(i.key));

  // ── Phone "Test" tab ─────────────────────────────────────────────────────────
  // Only ever rendered while `isPhone` (InputBlockFrame ignores it otherwise), and only replaces
  // the editingSlot content — everything else about the frame (titlebar, undo/redo, close) stays.
  // Same `computeDropdownTestData` the desktop collapsed card uses, fed `liveD` instead of
  // `initData(block.data)`: same live test STATE (rolledValues/testSelectedId/hasSubmitted/
  // continuous — all plain top-level hooks, never gated behind isEditing) either way, just a
  // different (here, more current — no separate mount to fall behind) data source.
  const phoneTest = isPhone
    ? computeDropdownTestData(liveD, variables, t, rolledValues, submittedSelectedId, hasSubmitted, i18n.language, activeScenario?.scenarioId)
    : null;
  const phoneHasChipsRollable = phoneTest ? (
    (htmlHasVariableChips(liveD.question) || (liveD.optionsAsEquations
      ? liveD.options.some(o => getEquationVariableNames(o.formula ?? '').length > 0)
      : liveD.options.some(o => htmlHasVariableChips(o.text)))) &&
    hasRollableVariables(variables)
  ) : false;
  // The phone Test tab renders the SAME DropdownAnswerDisplay as Preview and the canvas card. It used
  // to reimplement it line for line — provider, question, RichOptionSelect, fired feedback — which is
  // how a surface drifts: three copies, three chances to fix only two of them.
  const phoneTestSlot = phoneTest ? (
    <DropdownAnswerDisplay
      question={liveD.question}
      options={phoneTest.resolvedD.options}
      optionOrder={testOptionOrder}
      rolledValues={rolledValues}
      value={testSelectedId}
      onChange={handleTestChange}
      placeholder={liveD.placeholder}
      /* The phone needs this most: the leader is never on screen beside the block. */
      scenario={<ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />}
    >
      {renderDropdownFiredFeedback(phoneTest.testResult, rolledValues)}
    </DropdownAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: (phoneHasChipsRollable || randomizeOrder) ? handleTestReroll : undefined,
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={testPager?.versions ?? shownVersions}
        activeVersionId={testPager ? testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here, unlike the canvas card's: on a phone, Edit and Test are two
           tabs over ONE editing session and this tab renders the editor's live fields, so a
           separate test pointer would name one version while showing another. A FOLLOWER has
           only one pointer to begin with — the leader's — so the distinction does not arise. */
        onSelectVersion={testPager?.onSelect ?? selectVersion}
        noun={testPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // ── Feedback branch scroller ──────────────────────────────────────────────────
  // Same shared activeBranchIndex the setup carousel already uses, in a separate control here so
  // switching branches can never accidentally leave the two views out of sync — one always
  // reflects the other. Mirrors Numerical's own sticky test-controller-above-the-feedback-panel
  // pattern (feedbackTestController) rather than inventing a new placement convention.

  // Graded-only, like every other branching surface (activeBranch above, showConditionalUI in the
  // setup carousel): branches are read straight from persisted data, so a block authored with
  // branches and later switched to Participation/Optional kept showing this scroller above a
  // feedback panel whose states those branches can never reach.
  /**
   * The same bar for a follower, naming the scenario instead of a comparison.
   *
   * Feedback is per answer setup either way — "feedbacks live and die with the branching corrects"
   * — so the panel below is already showing one row's feedback and the teacher needs to know
   * which. Browse-only `VersionSelector` again: the rows are the leader's to add and remove.
   */
  const feedbackScenarioScroller = activeScenario ? (
    <div className='flex items-center justify-center gap-2 border-b border-ws-border bg-ws-surface px-4 py-2'>
      <span className='truncate text-xs font-medium text-ws-muted'>{activeScenarioVersion?.name}</span>
      {scenarioPager}
    </div>
  ) : null;

  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // ── Editing shell ────────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<SquareChevronDown className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.dropdown.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.dropdown.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            <CheckboxRow fieldId='randomizeOrder'
              checked={randomizeOrder}
              onToggle={() => { const next = !randomizeOrder; setRandomizeOrder(next); push(options, next); }}
              label={t('blocks.optionsFields.randomizeOrder')}
            />
            <div className='flex flex-col gap-1.5'>
              <label htmlFor='dropdown-placeholder-input' className='text-sm text-ws-text'>{t('blocks.dropdown.placeholderLabel')}</label>
              <TextField
                variant='prose'
                id='dropdown-placeholder-input'
                fieldId='placeholder'
                value={placeholder}
                onChange={v => { setPlaceholder(v); push(options, randomizeOrder, gradingMode, v); }}
                ariaLabel={placeholderDefault}
                placeholder={placeholderDefault}
                className='w-full' />
            </div>
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.dropdown.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: feedbackScenarioScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
          isEmpty: visibleTargetedStates.length === 0,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            {/* A bank is a niche feature: given its own row it was the first thing the eye landed
                on. In the heading it is available without being advertised, and the heading
                matches ANSWER SETUP below so the editor gains a section rather than a widget. */}
            {/*
              * THE SCENARIO, ABOVE THE QUESTION — the order a student meets them in, since the
              * leader renders above this block in the real task.
              *
              * It was below, between the question and the answers, and a multi-paragraph stem
              * there meant scrolling up to re-read the question on every row. See
              * `ScenarioRowFrame`'s note for why read-only context belongs here and the
              * selector stays with the answers.
              */}
            {/* The three-way offer, when linking meets an existing bank — §7.1. Rendered from
                here so it is inside the editor it is about. */}
            {remapModal}
            {/* The link control and what it points at, for BOTH kinds of link — see
                `VariesWithSection`, which decides on its own whether a DRIVEN block
                needs it. This was five lines of identical JSX in twelve blocks. */}
            <VariesWithSection
              following={!!activeScenario}
              linkMenu={scenarioLinkMenu}
              stimulus={<ScenarioStimulus leader={leaderBlock} version={activeScenarioVersion as Record<string, unknown> | null} />}
            />
            {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
                the answer setup, because a driven axis varies the question too. Renders its
                children bare when this block is not driven. See `DrivenRowFrame`. */}
            <DrivenRowFrame block={block} data={storedData} emit={emit}>
            <EditorSectionHeading
              label={t('blocks.areas.question')}
              /* First heading in the panel unless a scenario is above it. */
              divider={!!activeScenario}
              action={(
                /* `gap-2`: the pager's armed ring bleeds 5px (4px of padding plus a 1px
                   outline, which draws outside the box). `gap-3` cleared it too and pushed the
                   trash past the panel's edge. */
                <div className='flex items-center gap-2'>
                {/* The LINK sits after the pager, not before it — the owner's call, and it
                    reads better for a reason worth keeping: the pager belongs to the heading
                    it is paging, so "Question < 1 of 6 > (delete)" is one thought, and the
                    link is a separate one that belongs at the end of the row rather than
                    wedged between the label and the control it has nothing to do with. */}
                {/* The question bank is GONE while this block follows a scenario, not merely
                    inert — §2.2, and the owner's typing argument behind it: versions are many
                    stems each with one answer setup, a scenario is one stem with many. Offering
                    "make another version of this question" to a follower invites the teacher to
                    build the second structure inside the first, and the two cannot both be the
                    answer key. The rows in ANSWER SETUP are where a follower varies. */}
                {!storedData.followsBlockId && (
                <VersionSelector
                  versions={storedData.versions ?? []}
                  activeId={editorVersionId}
                  onSelect={selectVersion}
                  onAdd={() => {
                    const next = addVersion(initData(block.data, placeholderDefault), editorVersion, () => crypto.randomUUID());
                    emit(versionStructure(block.data, next));
                    // Land on the one just made: "+" means write a DIFFERENT question.
                    const added = next.versions?.[next.versions.length - 1];
                    if (added) selectVersion(added.id, next);
                  }}
                  onRemove={(id) => {
                    const next = removeVersion(initData(block.data, placeholderDefault), id);
                    emit(versionStructure(block.data, next));
                    const landing = next.versions?.[0];
                    selectVersion(landing?.id, next);
                    setTestVersion(landing?.id);
                  }}
                />
                )}
                {/* Silent once there is a Varies-With section, which is where the
                    control moves to — see `QuestionHeadingLink`. Written as
                    `!activeScenario` it knew about scenarios only, so a DRIVEN block
                    showed the link twice and the heading overflowed sideways. */}
                <QuestionHeadingLink
                  following={!!activeScenario}
                  linkMenu={scenarioLinkMenu}
                />
                </div>
              )}
            />
            <BlockQuestionEditor
              value={questionHtml}
              onChange={html => { questionHtmlRef.current = html; setQuestionHtml(html); push(); }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {(() => {
              // Meaningless outside graded mode — survey/optional never consult an option's role
              // (see evaluateDropdown), so there's nothing for a branch to conditionally set.
              // Question-only (see the auto-clear effect's own doc comment for why this is
              // deliberately NOT ORed with hasVariableInOptions below).
              /*
               * THE VARIABLE MAY BE IN THE SCENARIO RATHER THAN THE QUESTION.
               *
               * A follower's stem lives in the leader, so a block can have a perfectly static
               * question over a scenario full of chips. Reading only the question hid the
               * branching toggle from exactly the blocks that need it most.
               */
              const scenarioHtml = (shownScenarioVersion as { html?: string } | null)?.html ?? '';
              const hasVariable = htmlHasVariableChips(questionHtmlRef.current) || htmlHasVariableChips(scenarioHtml);
              /*
               * FOLLOWING NO LONGER RULES BRANCHING OUT. When both are on the branching nests
               * INSIDE the scenario row: the scenario picks the row, the row's own equation
               * picks the answer. See `use-nested-branching`.
               */
              const showConditionalUI = hasVariable && gradingMode === 'graded';
              // Specifically about OPTIONS, not the question — this is what shows the "treat
              // options as equations" toggle, independent of showConditionalUI/branching above.
              // Checked against `formula` too (not just `text`), so the toggle doesn't disappear
              // once a teacher is already using it and happens to clear a text-mode chip.
              const hasVariableInOptions = mayVarMechanism
                && options.some(o => htmlHasVariableChips(o.text) || getEquationVariableNames(o.formula ?? '').length > 0);
              /*
               * A RECALL OPENS IT TOO, and until now nothing did but a variable.
               *
               * The mechanism is "each option's CONTENT is computed", and a recalled answer is
               * as good a thing to compute from as a rolled variable — better, for error carried
               * forward. But the only door into equations mode was a variable chip in an option,
               * so a teacher with Answer Recall on and Variables off could not put a recall into
               * an option at all. Asked for in those words: "i want to be able to put recall
               * chips into mcq/dropdown options, just like variables".
               *
               * Gated on the RECALL capability, not on the variables mechanism: they are
               * different capabilities and this is the half that belongs to recall.
               */
              /* A NUMBER'S alias, and only that. The mechanism computes a value, so a
                 recall that resolves to words opens nothing — it is already working where it
                 stands, in the option's own text. */
              const hasRecallInOptions = mayRecallHere
                && options.some(o => (
                  recallAliasIdsIn(o.text).some(id => numericAliasIds.has(id))
                  || hasRecallChip(o.formula ?? '')));
              // Shown whenever a trig call appears in EITHER formula surface — one shared mode for
              // the whole block (see DropdownBlockData.angleMode), not per-formula, so it's a single
              // check across both rather than two independent toggles that could disagree.
              const hasTrigAnywhere = TRIG_CALL_PATTERN.test(conditionalEquation ?? '') ||
                options.some(o => TRIG_CALL_PATTERN.test(o.formula ?? ''));
              const isEnabled = conditionalBranches !== undefined;
              const branches = conditionalBranches ?? [];
              const clampedIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
              // Gated on gradingMode, not just isEnabled — this is a SEPARATE local shadowing the
              // outer top-level `activeBranch` (same name, different scope), and it's the one that
              // actually feeds the options-list editor's role display/onRoleClick below. Without
              // this gate, a branch's stale roles kept showing as "Correct answer" on an option in
              // Participation/Optional even after the outer activeBranch was fixed to ignore it.
              const activeBranch = isEnabled && gradingMode === 'graded' ? branches[clampedIndex] : undefined;
              /* WHOSE branch set is on screen — the row's when following, the block's otherwise. */
              const { nesting, effEquation, effEnabled, effBranches, effIndex, effActiveBranch } =
                nestedBranching<DropdownFeedback>({
                  activeScenario,
                  blockEquation: conditionalEquation,
                  blockBranches: branches,
                  blockEnabled: isEnabled,
                  activeBranchIndex,
                  graded: gradingMode === 'graded',
                });
              const makeRow = (mode: SeedMode) => makeRowBranch<DropdownFeedback>({
                mode,
                roles: activeScenario?.roles ?? {},
                feedback: activeScenario!.feedback,
                cloneFeedback: cloneFeedbackWithFreshIds,
                seedFeedback: f => makeConditionalBranch(f, 'fork').feedback,
              });
              /** Every write to the row's own branching — deciding it IS reviewing it (§4.4). */
              const writeRow = (patch: Partial<ScenarioRoleBranch<DropdownFeedback>>) => {
                if (!activeScenario) return;
                pushScenarioRows(markScenarioReviewed(
                  (scenarioBranches ?? []).map(b =>
                    (b.scenarioId === activeScenario.scenarioId ? { ...b, ...patch } : b)),
                  activeScenario.scenarioId,
                ));
              };
              /** One toggle, two scopes — the ROW's while following, the block's otherwise. */
              const toggleRowBranching = () => {
                if (!nesting) { toggleConditionalCorrectness(!isEnabled); return; }
                setActiveBranchIndex(0);
                writeRow(effEnabled
                  ? { conditionalEquation: undefined, conditionalBranches: undefined }
                  : { conditionalEquation: activeScenario!.conditionalEquation ?? '',
                      conditionalBranches: [makeRow('migrate')] });
              };
              // The row the option list below actually edits. A scenario and an equation are two
              // selectors and the BLOCK carries only one, so these are never both set — the
              // `??` states the precedence rather than relying on that.
              /*
               * WHERE THE TOGGLE SITS IS A CLAIM ABOUT WHAT IT APPLIES TO. On a follower it
               * turns branching on for ONE ROW, so standing above the scenario box — beside
               * block-wide settings — reads as "enable this for every scenario".
               */
              const branchingToggle = showConditionalUI ? (
                <BranchingCorrectsRow
                  checked={effEnabled}
                  onToggle={toggleRowBranching}
                />
              ) : null;
              const blockLevelToggle = showConditionalUI && !nesting;
              const activeRow = effActiveBranch ?? activeScenario ?? activeBranch;
              // A between branch with max < min never matches anything (see branchMatches), which
              // would otherwise only surface indirectly as a coverage gap — flag it directly at
              // the inputs themselves instead, same as Numerical's own between-answer check.

              // Every threshold/operator can be fully configured with no equation ever entered —
              // ghost the carousel and suppress coverage/role warnings together in that case (same
              // visible-but-inert treatment as the rich-text toolbar) rather than letting them read
              // as live and meaningful when nothing is actually being evaluated yet.
              const hasEquation = !!conditionalEquation?.trim();
              // Validated the same way it's actually resolved at runtime (resolveEffectiveOptions/
              // evaluateEquation) — a typo'd function name (e.g. "sing(23)") or an unknown variable
              // parses as non-empty text but never evaluates to a number, so `hasEquation` alone
              // can't tell a working equation from a broken one. minSampleValues, NOT `rolledValues`
              // — that state stays {} until the block is actually isSelected (the live-test widget,
              // see its declaration above), which would make every equation referencing a variable
              // look invalid the entire time it's being authored.
              const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;
              const equationWorks = hasEquation && !equationInvalid;

              // Per-option error highlighting for the two new formula checks — same
              // dropdownIssues computation, just re-derived here as a Set for O(1) per-row
              // lookup rather than re-scanning the whole issues array once per option row.
              const optionFormulaIssueIds = new Set(
                optionsAsEquations
                  ? checkOptionFormulaIssues(
                      options,
                      variables,
                      angleMode
                    ).issues.flatMap(i => i.optionIds)
                  : []
              );

              // The exact same option editor (drag reorder, text, anchor, remove) used whether or
              // not conditional correctness is on — only which role source feeds each row's
              // correct/incorrect button changes. Adding/removing/reordering/renaming an option is
              // never per-rule, it's universal across every rule setup, so those handlers are
              // always the plain top-level ones regardless of `activeBranch`.
  // Rendered WITH the list in both states, the pattern Scale already used. Adding an option is an
  // answer-set operation, so the control belongs with the answer set — and when branching is on the
  // answer set lives inside the carousel, which is where the button sits rather than across a
  // border from the list it appends to.
  //
  // It is global, not per-branch: an option added here appears in every condition. That reads as a
  // scope claim only if you believe the carousel bounds a scope, and it does not — the option text,
  // formula and media edited inside it are equally global, and only the role toggle varies.
  const addOptionButton = (
          <AddRowButton
            ref={addOptionButtonRef}
            label={t('blocks.options.addOption')}
            onClick={addOption}
          />
  );

              const optionListJsx = (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleOptionDragEnd}
                  accessibility={dndA11y(t, options.map(x => x.id), 'option')}
    >
                  <SortableContext items={options.map(o => o.id)} strategy={verticalListSortingStrategy}>
                    <div className='flex flex-col gap-3'>
                      {options.map(opt => (
                        <SortableOptionRow
                          key={opt.id}
                          opt={{ ...opt, role: roleForMode(gradingMode, activeRow ? activeRow.roles[opt.id] ?? 'incorrect' : opt.role) }}
                          optionCount={options.length}
                          roleLocked={gradingMode !== 'graded'}
                          showAnchor={randomizeOrder}
                          armedOptionId={armedOptionId}
                          setArmedOptionId={setArmedOptionId}
                          onRoleClick={activeScenario ? (id) => handleScenarioRoleClick(activeScenario.scenarioId, id)
                            : activeBranch ? (id) => handleBranchRoleClick(activeBranch.id, id) : handleRoleClick}
                          onToggleAnchor={toggleAnchor}
                          onTextChange={updateOptionText}
                          onRemove={removeOption}
                          optionEditorsRef={optionEditorsRef}
                          activeEditorRef={activeEditorRef}
                          setActiveEditor={setActiveEditor}
                          formulaMode={optionsAsEquations}
                          onFormulaChange={updateOptionFormula}
                          formulaHasError={optionFormulaIssueIds.has(opt.id)}
                          hasDuplicate={collidingOptionIds.has(opt.id)}
                          variables={variables}
                          rolledValues={rolledValues}
                          angleMode={angleMode}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              );

              return (
                <>
                  {/* Guarded as a whole, not just child by child: every child here is conditional, so an
                      unguarded wrapper became an empty flex item whose parent gap applied both above and
                      below it — a doubled gap under the Answer Setup rule whenever no variable was in
                      play. Matches Ordering/FITB/Scale/GPS, which already guard theirs. */}
                  {(blockLevelToggle || hasVariableInOptions || hasRecallInOptions || optionsAsEquations || hasTrigAnywhere) && (
                    <div className='flex flex-col gap-2 pl-1'>
                      {blockLevelToggle && branchingToggle}

                      {/* `|| optionsAsEquations` keeps this checkbox reachable once it's already on,
                          even after every option variable is removed — sticky by design (2026-08-02
                          standing rule, see MCQ/Ordering's own equivalent comment): once a teacher
                          turns this on, it should only ever turn off via an explicit click, never a
                          precondition-loss auto-clear, since that precondition is the SAME field the
                          toggle governs — losing it mid-authoring would otherwise flip options back
                          to rich text out from under the teacher's cursor. */}
                      {(hasVariableInOptions || hasRecallInOptions || optionsAsEquations) && (
                        <CheckboxRow fieldId='optionsAsEquations'
                          checked={optionsAsEquations}
                          onToggle={() => {
                            const next = !optionsAsEquations;
                            // Symmetric content-flow (2026-08-02 standing rule, mirrors MCQ's own
                            // identical toggle — see its doc comment for the full reasoning; Dropdown
                            // options have no media field at all, so only the formula/text half of
                            // that rule applies here):
                            // ON: an option's plain text transfers into `formula` when that text is
                            // ALREADY a valid, parseable formula (e.g. "{n}+10" with no surrounding
                            // prose), verified by actually evaluating it, not guessed from syntax —
                            // a genuine sentence is correctly left with a BLANK formula. Never
                            // overwrites an already-authored formula.
                            // OFF: every option's CURRENT formula (blank or not) is copied back into
                            // `text` as real rich content (a genuine re-editable chip for any
                            // `{name}` it contains) — so a round trip with nothing re-authored in
                            // between ends up blank, not restored to whatever prose the option
                            // originally had before turning equations mode on.
                            const nextOptions = next
                              ? options.map(o => {
                                  if (o.formula?.trim()) return o;
                                  /*
                                   * BRACKETISED FOR A FORMULA, not stripped for a LABEL. The
                                   * label path leaves a recall chip alone so a feedback title
                                   * reads "You said Chinchilla" — through this toggle that
                                   * dropped the reference and kept the word.
                                   *
                                   * AND A TEXT RECALL STOPS THE TRANSFER OUTRIGHT. A calculated
                                   * option is arithmetic, and `FormulaField` draws a chip only
                                   * for a number — a word's alias became an opaque
                                   * `[ara_<uuid>]` in the field, unreadable and undeletable as
                                   * a unit. Refusing the whole option rather than quietly
                                   * dropping the chip and keeping the rest: `2 + 3 {critter}`
                                   * would otherwise become a perfectly valid 5 that nobody
                                   * wrote. The text is left where it is, which is where a word
                                   * belongs.
                                   */
                                  const stripped = optionTextToFormula(o.text, numericAliasIds);
                                  const wordRecall = recallAliasIdsIn(o.text)
                                    .some(id => !numericAliasIds.has(id));
                                  /* A STAND-IN 1 PER RECALL, which is `probeValuesForRecalls`'
                                     whole job: the question here is "is this arithmetic at all",
                                     not what it comes out as, and a recall has no value at
                                     authoring time. Without it every option holding one read as
                                     un-transferable and was blanked by the toggle. */
                                  const probe: Record<string, number> = { ...minSampleValues(variables) };
                                  for (const id of Object.keys(probeValuesForRecalls(stripped))) probe[id] = 1;
                                  const transferable = !wordRecall && stripped.trim() !== ''
                                    && evaluateEquation(stripped, probe, angleMode) !== null;
                                  return transferable ? { ...o, formula: stripped } : { ...o, formula: '' };
                                })
                              : options.map(o => ({ ...o, text: formulaToRichText(o.formula ?? '', new Set(variables.map(v => v.name)), knownAliasIds) }));
                            setOptionsAsEquations(next);
                            setOptions(nextOptions);
                            // Every targeted state's title is derived from whichever source
                            // (text or formula) is active — flipping the toggle without resyncing
                            // left titles baked under the OLD mode until some unrelated edit
                            // happened to trigger a resync (same bug found+fixed in MCQ's own
                            // identical toggle — see mcq-block.tsx).
                            const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
                              states => resyncDropdownTargetedTitles(nextOptions, states, t, next)
                            );
                            setFeedback(nextFeedback);
                            if (nextBranches) setConditionalBranches(nextBranches);
                            push(nextOptions, randomizeOrder, gradingMode, placeholder, nextFeedback, nextBranches ?? conditionalBranches, conditionalEquation, next);
                          }}
                          label={t('blocks.options.optionsAsEquationsToggle')}
                        />
                      )}

                    </div>
                  )}

                  {showConditionalUI && effEnabled && effActiveBranch && !nesting ? (
                    <BranchConditionCarousel
                      seedVariable={(() => { const n = variableChipNames(questionHtmlRef.current); return n.length === 1 ? n[0] : undefined; })()}
                      ref={branchPrevButtonRef}
                      equation={conditionalEquation ?? ''}
                      onEquationChange={updateConditionalEquation}
                      variables={variables}
                      rolledValues={rolledValues}
                      angleMode={angleMode}
                      equationInvalid={equationInvalid}
                      equationWorks={equationWorks}
                      hasError={hasEditorError('no-equation', 'invalid-equation', 'coverage-gap', `branch-${effActiveBranch!.id}-multi`)}
                      branches={branches}
                      activeIndex={clampedIndex}
                      onActiveIndexChange={setActiveBranchIndex}
                      onBranchChange={updateConditionalBranch}
                      onAdd={addConditionalBranch}
                      onRemove={removeConditionalBranch}
                      onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    >
                      {/* No box. The carousel already draws the only boundary there is, and a
                          second border inside it implied a second scope — which the data model does
                          not have: a branch carries ROLES only, while the option text, formula and
                          media edited here are global and change in every branch at once. */}
                      {optionListJsx}
                      {addOptionButton}
                    </BranchConditionCarousel>
                  ) : (
                    /* The scenario's own box, which is the carousel's shape with a variant in
                       place of an equation — and nothing at all when this block follows
                       nothing. Same reason for the answers being INSIDE it: the box is what
                       says which scope they belong to. */
                    <ScenarioSetupFrame
                      scenario={activeScenario}
                      name={activeScenarioVersion?.name}
                      pager={scenarioPager}
                      unreviewedCount={unreviewedCount(scenarioBranches)}
                      onDismissUnreviewed={() => pushScenarioRows(
                        markScenarioReviewed(scenarioBranches ?? [], activeScenario!.scenarioId))}
                    >
                      {/* Only when there IS a box. `ScenarioSetupFrame` passes its children
                          through unwrapped when this block follows nothing, so an ungated
                          toggle here renders a SECOND copy beside the block-level one. */}
                      {nesting && branchingToggle && <div className='pb-2'>{branchingToggle}</div>}
                      {showConditionalUI && effEnabled && effActiveBranch ? (
                        <BranchConditionCarousel
                          seedVariable={(() => {
                            const all = [...new Set([...variableChipNames(questionHtmlRef.current), ...variableChipNames(scenarioHtml)])];
                            return all.length === 1 ? all[0] : undefined;
                          })()}
                          equation={effEquation ?? ''}
                          onEquationChange={v => writeRow({ conditionalEquation: v })}
                          variables={variables}
                          rolledValues={rolledValues}
                          angleMode={angleMode}
                          equationInvalid={false}
                          equationWorks={!!effEquation?.trim()}
                          hasError={false}
                          branches={effBranches}
                          activeIndex={effIndex}
                          onActiveIndexChange={setActiveBranchIndex}
                          onBranchChange={(id, patch) => writeRow({
                            conditionalBranches: effBranches.map(b => (b.id === id ? { ...b, ...patch } : b)) as never })}
                          onAdd={() => writeRow({ conditionalBranches: [...effBranches, makeRow('fork')] as never })}
                          onRemove={id => writeRow({
                            conditionalBranches: effBranches.filter(b => b.id !== id) as never })}
                          onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                        >
                          {optionListJsx}
                          {addOptionButton}
                        </BranchConditionCarousel>
                      ) : (<>
                        {optionListJsx}
                        {addOptionButton}
                      </>)}
                    </ScenarioSetupFrame>
                  )}
                </>
              );
            })()}

            </DrivenRowFrame>
          </div>
      }
    />
  );
}
